/**
 * ClauseArk marketing content — single source of truth for all page copy.
 *
 * Voice: confident · clear · concise · honest · human.
 * Every claim on this site must match what's actually shipped in the product.
 * No invented stats, no fake customers, no testimonials we don't have.
 *
 * ── Sweep changelog ─────────────────────────────────────────────────
 * 2026-04-29 (Task #584): Added the "AI you actually own" positioning
 *   page (`/platform/ai`) and its capability matrix as the new
 *   single-source-of-truth `AI_POSITIONING_PAGE`. Updated the Platform
 *   nav menu to surface it as a sibling of "Contract Intelligence",
 *   added an in-context cross-link from the Contract Intelligence
 *   platform page, and added a "Learn how our AI is different" tertiary
 *   link to the home hero. The pitch deck's HowWeCompare slide now
 *   reads from a mirrored copy of the same matrix at
 *   `artifacts/pitch-deck/src/data/positioningMatrix.ts`, with the
 *   `validate-positioning-truth` script asserting byte-equivalence
 *   between the two copies (so the deck never drifts from the
 *   website). Every "shipped" row in the matrix is paired with a
 *   `truthSelfChecks` keyword set the validator looks up in this file
 *   outside of `AI_POSITIONING_PAGE` — so we cannot claim something
 *   ships in the matrix without it being reflected on a buyer-facing
 *   capability list elsewhere in this content module. Competitor cells
 *   we cannot back up with public docs render `—` with the footnote
 *   spelled out in `AI_POSITIONING_PAGE.matrix.unverifiedFootnote`.
 * 2026-04-29 (Task #570): Multi-Document Deal Reasoning shipped. Added a
 *   "Multi-document deal review" capability bullet on Contract
 *   Intelligence (deal bundles of MSA + SOW + DPA + Order Form,
 *   cross-document detectors for counterparty mismatch, governing law,
 *   insurance / payment / term divergence, missing DPA, definition
 *   drift) and a matching `whatYouGet` line on the In-House Legal
 *   solutions page. No other surfaces touched.
 * 2026-04-29 (Task #569): Shipped the autonomous renewal agent. Ninety
 *   days before each renewal the agent gathers comparable past
 *   renewals, summarises leverage, drafts a strategy memo, and
 *   prepares a redlined draft; approval-workflow and signature-route
 *   steps always pause for a human reviewer. New "Autonomous renewal
 *   agent" capability added under PLATFORM_PAGES.workflow-automation
 *   to reflect the shipped surface (12-call-per-run LLM cap, hash-
 *   chained audit log, tenant-level enable/lead-window/denylist
 *   controls).
 * 2026-04-28 (Task #444): Industry rebalance. The site now reads as a
 *   contract intelligence + lifecycle product for *contract teams* in
 *   general, with three first-class industries: Legal (covering
 *   In-House Legal and Law Firms), Construction, and Insurance. Shared
 *   pages (HOME, PLATFORM_PAGES, WORD_ADDIN_PAGE, PRICING, ABOUT,
 *   PRIMARY_NAV/FOOTER_NAV) describe the product in industry-neutral
 *   terms ("reviewers", "your team", "contract teams"). Industry
 *   Solutions pages (`/solutions/*`) carry the sharper, vertical
 *   vocabulary. Enterprise is now positioned as a buyer shape (security
 *   & SSO), not an industry. No product features were added — copy and
 *   structure only.
 * 2026-04-28 (Task #400): Delta sweep against shipped product. No
 *   merges have landed since the Task #399 catch-up sweep stamped
 *   earlier the same day, so the delta is empty — no marketing
 *   surfaces (home cards, platform / solutions pages, integrations
 *   list, FAQs, pricing) needed an update. Cutoff re-stamped to
 *   today so the next run has a clean reference point.
 *
 * 2026-04-28 (Task #399): One-time catch-up sweep against the live
 *   product. Pricing on this page is the source of truth and matches
 *   `lib/db/src/schema/planLimits.ts` (Startup $0/3 seats/50 yr,
 *   Growth $800/15 seats/500 yr, Enterprise custom; vertical Solo
 *   $199/1 seat/25 yr, Team $499/3 seats/100 yr, Pro $1,200/8 seats/
 *   400 yr; payg_review metered at $29/contract). Verified shipped:
 *   Adobe Sign + Dropbox Sign, Pipedrive + Microsoft Dynamics CRM,
 *   ERP connectors (NetSuite, SAP Ariba, Workday, Coupa), Outlook +
 *   Gmail mailbox scanner, DMS mirror writeback, hash-chained audit
 *   log (audit_log.prev_hash + .hash, populated by auditWriter, used
 *   by the framework-report finding generator). Still on the deck
 *   roadmap (genuinely not shipped): SOC 2 Type II report, Microsoft
 *   Teams notifications, BYOS, deep DMS sync, AI negotiator.
 * ────────────────────────────────────────────────────────────────────
 */

import type { ComponentType } from "react";

export const BRAND = {
  name: "ClauseArk",
  tagline: "Your contracts, protected.",
  shortTagline: "Contracts, protected.",
  microTagline: "Protect what's promised.",
  productLine: "Contract review, redlining, and lifecycle for contract teams in Legal, Construction, and Insurance",
  email: "hello@clauseark.com",
  salesEmail: "sales@clauseark.com",
  supportEmail: "support@clauseark.com",
  social: {
    linkedin: "https://www.linkedin.com/company/clauseark",
    x: "https://x.com/clauseark",
    youtube: "https://www.youtube.com/@clauseark",
    github: "https://github.com/clauseark",
  },
} as const;

/* ──────────────────────────────────────────────────────────────────
 * Navigation
 * ────────────────────────────────────────────────────────────────── */

export type NavItem = {
  label: string;
  path?: string;
  description?: string;
  children?: NavItem[];
};

export const PRIMARY_NAV: NavItem[] = [
  {
    label: "Platform",
    children: [
      {
        label: "Contract Intelligence",
        path: "/platform/contract-intelligence",
        description: "Playbook-based AI review, risk scoring, executive summaries — bootstrap a starter playbook from your last 50 executed contracts.",
      },
      {
        label: "AI you actually own",
        path: "/platform/ai",
        description: "BYOK on your KMS, citation-backed redlines, audit-traceable agents, and a public Contract Agents SDK — what makes ClauseArk's AI different.",
      },
      {
        label: "Workflow Automation",
        path: "/platform/workflow-automation",
        description: "Matters, intake forms, workflow tasks, renewals dashboard.",
      },
      {
        label: "Approve in Chat",
        path: "/platform/chat-approvals",
        description: "Approve, reject, or comment on contracts directly in Slack and Microsoft Teams.",
      },
      {
        label: "Trust & Access",
        path: "/platform/compliance-engine",
        description: "SSO, BYOK, audit logs.",
      },
      {
        label: "Integrations",
        path: "/platform/integrations",
        description: "DocuSign, Adobe Sign, Salesforce, HubSpot, SharePoint, and more.",
      },
      {
        label: "Word add-in",
        path: "/word-addin",
        description: "AI contract review, redlines, and a clause library inside Microsoft Word.",
      },
      {
        label: "AI Copilot",
        path: "/platform/ai-copilot",
        description: "Chat with your contracts — ask questions in plain English and get cited answers.",
      },
    ],
  },
  {
    label: "Solutions",
    children: [
      {
        label: "Solutions overview",
        path: "/solutions",
        description: "Pick your industry — Legal, Construction, or Insurance — and see what ships for it today.",
      },
      {
        label: "Legal · In-House",
        path: "/solutions/in-house-legal",
        description: "Centralize the contract lifecycle for a lean in-house legal team.",
      },
      {
        label: "Legal · Law Firms",
        path: "/solutions/law-firms",
        description: "Cut first-pass review on routine paper for high-volume firms.",
      },
      {
        label: "Construction",
        path: "/solutions/construction",
        description: "AIA, ConsensusDocs, and flow-down for GCs, subs, and owners.",
      },
      {
        label: "Insurance",
        path: "/solutions/insurance",
        description: "Producer, MGA, TPA, and treaty playbooks with binding-authority extraction.",
      },
      {
        label: "Enterprise (security & SSO)",
        path: "/solutions/enterprise",
        description: "SSO, BYOK, and audit logs for security-led buyers in any industry.",
      },
    ],
  },
  {
    label: "Pricing",
    children: [
      {
        label: "Platform pricing",
        path: "/pricing",
        description: "Plans, tiers, and add-ons for the full ClauseArk platform.",
      },
      {
        label: "Construction pricing",
        path: "/pricing/construction",
        description: "Vertical pricing for construction GCs, subs, and owners.",
      },
      {
        label: "Insurance pricing",
        path: "/pricing/insurance",
        description: "Vertical pricing for brokers, MGAs, TPAs, and carriers.",
      },
    ],
  },
  {
    label: "Resources",
    children: [
      { label: "Blog", path: "/resources/blog" },
      { label: "Documentation", path: "/resources/docs" },
      { label: "Webinars", path: "/resources/webinars" },
      {
        label: "Trust",
        path: "/trust",
        description: "Security posture, sub-processors, encryption, data residency, and the security review packet.",
      },
    ],
  },
  {
    label: "Company",
    children: [
      { label: "About", path: "/company/about" },
      { label: "Careers", path: "/company/careers" },
      { label: "Contact", path: "/company/contact" },
    ],
  },
];

export const FOOTER_NAV: { title: string; items: NavItem[] }[] = [
  {
    title: "Platform",
    items: [
      { label: "Contract Intelligence", path: "/platform/contract-intelligence" },
      { label: "AI you actually own", path: "/platform/ai" },
      { label: "Workflow Automation", path: "/platform/workflow-automation" },
      { label: "Approve in Chat", path: "/platform/chat-approvals" },
      { label: "Trust & Access", path: "/platform/compliance-engine" },
      { label: "Integrations", path: "/platform/integrations" },
      { label: "Word add-in", path: "/word-addin" },
      { label: "AI Copilot", path: "/platform/ai-copilot" },
    ],
  },
  {
    title: "Solutions",
    items: [
      { label: "Solutions overview", path: "/solutions" },
      { label: "Legal · In-House", path: "/solutions/in-house-legal" },
      { label: "Legal · Law Firms", path: "/solutions/law-firms" },
      { label: "Construction", path: "/solutions/construction" },
      { label: "Insurance", path: "/solutions/insurance" },
      { label: "Enterprise (security & SSO)", path: "/solutions/enterprise" },
    ],
  },
  {
    title: "Pricing",
    items: [
      { label: "Platform pricing", path: "/pricing" },
      { label: "Construction pricing", path: "/pricing/construction" },
      { label: "Construction add-ons ROI", path: "/pricing/construction/add-ons" },
      { label: "Insurance pricing", path: "/pricing/insurance" },
    ],
  },
  {
    title: "Resources",
    items: [
      { label: "Blog", path: "/resources/blog" },
      { label: "Documentation", path: "/resources/docs" },
      { label: "Webinars", path: "/resources/webinars" },
    ],
  },
  {
    title: "Company",
    items: [
      { label: "About", path: "/company/about" },
      { label: "Careers", path: "/company/careers" },
      { label: "Contact", path: "/company/contact" },
    ],
  },
  {
    title: "Legal",
    items: [
      { label: "Trust", path: "/trust" },
      { label: "Privacy Policy", path: "/legal/privacy" },
      { label: "Terms of Service", path: "/legal/terms" },
      { label: "Security", path: "/legal/security" },
      { label: "Cookies", path: "/legal/cookies" },
    ],
  },
];

/* ──────────────────────────────────────────────────────────────────
 * Home
 * ────────────────────────────────────────────────────────────────── */

export const HOME = {
  hero: {
    eyebrow: "Contract review & lifecycle",
    headline: "Your contracts, protected.",
    subhead:
      "ClauseArk reviews, redlines, and tracks contracts against your playbook — from intake through signature and renewal. Built for contract teams in Legal, Construction, and Insurance.",
    primaryCta: { label: "Book a demo", path: "/demo" },
    secondaryCta: { label: "See pricing", path: "/pricing" },
    tertiaryLink: {
      label: "Learn how our AI is different",
      path: "/platform/ai",
    },
  },
  painPoints: {
    headline: "Contract work eats the day. Here's where it goes.",
    cards: [
      {
        icon: "Clock",
        title: "Repetitive first-pass review",
        body: "NDAs, MSAs, subcontracts, and producer agreements come in faster than your team can read them. Most of the work is checking the same clauses against the same playbook — over and over.",
      },
      {
        icon: "AlertTriangle",
        title: "Counterparty paper, your positions",
        body: "Their paper. Your fallbacks. Reconciling the two by hand — and then again on every counterparty turn — is where deals slow down and risk creeps in.",
      },
      {
        icon: "FileWarning",
        title: "Renewals you find out about late",
        body: "Termination windows live in PDFs no one re-reads. Auto-renewals fire before legal, ops, or finance get a chance to weigh in.",
      },
    ],
  },
  solutionOverview: {
    headline: "What ClauseArk actually does today",
    sub: "A working CLM you can ship next week — review, redline, and lifecycle in one place, plus a Word add-in for solo reviewers.",
    cards: [
      {
        icon: "FileSearch",
        title: "Contract Intelligence",
        body: "Upload PDFs or DOCX (with OCR for scans) or bulk-import from SharePoint, Box, Google Drive, iManage, or NetDocuments. We run playbook-based review with per-clause risk levels, an overall risk score, and an executive summary — in English, Spanish, French, German, Japanese, or Brazilian Portuguese.",
        cta: { label: "Learn more", path: "/platform/contract-intelligence" },
      },
      {
        icon: "Workflow",
        title: "Workflow & Lifecycle",
        body: "Organize work into matters. Take in new requests through public intake forms or an intake email. Run negotiation rounds, build approval workflows with tasks, and track renewals on a single dashboard.",
        cta: { label: "Learn more", path: "/platform/workflow-automation" },
      },
      {
        icon: "ShieldCheck",
        title: "Trust & Access",
        body: "SSO via WorkOS (SAML and OIDC), bring-your-own-key encryption with AWS KMS or Azure Key Vault, and an audit log of every action your tenant takes.",
        cta: { label: "Learn more", path: "/platform/compliance-engine" },
      },
      {
        icon: "Plug",
        title: "Integrations",
        body: "DocuSign, Adobe Sign, Dropbox Sign, and a native lightweight signing option. Salesforce and HubSpot for CRM. SharePoint, Box, Google Drive, iManage, and NetDocuments for document sources.",
        cta: { label: "Learn more", path: "/platform/integrations" },
      },
    ],
  },
  howItWorks: {
    headline: "From intake to signature, on one platform.",
    steps: [
      {
        n: "01",
        title: "Bring contracts in",
        body: "Upload PDFs and DOCX one at a time, in bulk from your computer, or pull them from SharePoint, Box, Google Drive, iManage, or NetDocuments. Scanned PDFs are OCR'd automatically.",
      },
      {
        n: "02",
        title: "Run AI review",
        body: "ClauseArk reads each contract against the playbook you choose, scores every clause for risk, gives the document an overall risk rating, and writes a short executive summary you can send up the chain.",
      },
      {
        n: "03",
        title: "Redline and negotiate",
        body: "Accept, edit, or reject the AI's suggested redlines tied to your playbook positions. Import a counterparty's revised .docx and ClauseArk matches their changes back to the clauses they affect — then writes a plain-English summary of what they actually changed (economic, legal-risk, scope, or cosmetic) so you don't have to re-read the whole doc. One click re-papers their draft onto your house template, preserving the negotiated parties, fees, term, and governing law.",
      },
      {
        n: "04",
        title: "Sign and renew",
        body: "Send for signature through DocuSign, Adobe Sign, Dropbox Sign, or our native signing flow. Track renewal dates on the renewals dashboard so nothing auto-renews silently.",
      },
    ],
  },
  productPillars: {
    headline: "What you get on day one",
    sub: "Every feature listed here is shipped today — not a roadmap promise.",
    items: [
      {
        title: "Playbook-based review",
        body: "Per-clause risk levels, an overall risk score, an executive summary, and jurisdiction-aware notes for each contract you upload.",
      },
      {
        title: "Bulk and connector intake",
        body: "Bulk upload from your computer or import from SharePoint, Box, Google Drive, iManage, and NetDocuments — with OCR for scanned PDFs.",
      },
      {
        title: "AI redlines tied to your playbook",
        body: "Suggested edits trace back to specific playbook positions, not a generic style guide — and each one cites up to three executed-contract precedents from your own workspace so reviewers can see the exact prior wording the AI is matching. Per-clause history travels with each contract.",
      },
      {
        title: "Negotiation rounds with counterparty .docx",
        body: "Import their revised Word doc and ClauseArk matches the changes back to your clauses so you can review what actually moved. One click re-papers their draft onto your house template — negotiated parties, fees, term, governing law, and definitions are preserved as a tracked-changes round.",
      },
      {
        title: "Matters, intake forms, and renewals",
        body: "Group contracts into matters. Stand up a public intake form or an intake email address. Watch renewals on a dedicated dashboard.",
      },
      {
        title: "Multi-language review",
        body: "Run review in English, Spanish, French, German, Japanese, or Brazilian Portuguese — all on the same playbook.",
      },
      {
        title: "Industry packs",
        body: "Tenant-level packs for Construction and Insurance unlock industry-specific contract types and side-aware playbooks on top of the General default — for in-house legal, GCs and subs, and brokers and carriers alike.",
      },
    ],
  },
  industries: {
    eyebrow: "Solutions",
    headline: "Three industries, one product.",
    sub: "ClauseArk ships with a General playbook out of the box and tenant-level packs for Construction and Insurance. Each industry's Solutions page covers the contract types, playbooks, and outcomes that matter to it.",
    cards: [
      {
        industry: "Legal",
        body: "In-house legal teams and high-volume law firms — NDAs, MSAs, DPAs, vendor and procurement paper, with side-aware playbooks for inbound and outbound.",
        links: [
          { label: "In-House Legal", path: "/solutions/in-house-legal" },
          { label: "Law Firms", path: "/solutions/law-firms" },
        ],
      },
      {
        industry: "Construction",
        body: "GCs, subs, and owners — AIA A201/A401/A101 and ConsensusDocs 200/750 pre-loaded, with a flow-down checker and state lien & notice jurisdiction notes.",
        links: [{ label: "Construction", path: "/solutions/construction" }],
      },
      {
        industry: "Insurance",
        body: "Carriers, MGAs, sub-producers, TPAs, and reinsurers — six side-aware playbooks, binding-authority extraction off Schedule C, and YoY renewal diff.",
        links: [{ label: "Insurance", path: "/solutions/insurance" }],
      },
    ],
    overviewCta: { label: "See all Solutions", path: "/solutions" },
  },
  wordAddinSpotlight: {
    eyebrow: "Word add-in",
    headline: "AI contract review, right inside Microsoft Word.",
    body: "ClauseArk's Word add-in runs playbook-based review on the document you have open, drops AI redlines in as Word tracked changes with rationale in comments, gives you a clause library you can insert at the cursor, and one-click re-papers the counterparty's draft onto your house template — all without ever leaving Word.",
    bullets: [
      "Playbook-based review, risk scoring, and an executive summary inside the doc",
      "AI redlines applied as Word tracked changes, with the rationale in comments",
      "A clause library tab to insert pre-approved language at the cursor",
      "One-click \"Transform to our template\" re-papers the counterparty draft onto your house paper, preserving negotiated parties, fees, term, and governing law",
    ],
    primaryCta: { label: "See the Word add-in", path: "/word-addin" },
    secondaryCta: { label: "Join the waitlist", path: "/word-addin#waitlist" },
  },
  reviewOnTheGo: {
    eyebrow: "Mobile approvals",
    headline: "Review and approve from anywhere.",
    body: "Add ClauseArk to your phone's home screen and your queue follows you. Push notifications land the moment a contract needs your call; the executive summary, risk score, and approve / reject buttons are one tap deep.",
    bullets: [
      "Installable on iPhone and Android — no app store, no MDM",
      "Push notification when an approval lands in your queue",
      "Executive summary, risk score, and one-tap approve / reject",
    ],
  },
  beliefs: {
    headline: "What we believe about contract software",
    items: [
      {
        title: "Plain text over hype",
        body: "If a feature isn't shipped, it doesn't belong on this site. We'd rather underclaim than ship a slide that doesn't survive a demo.",
      },
      {
        title: "Your playbook is the source of truth",
        body: "AI suggestions are useful only when they map to a position you can defend. Every redline ClauseArk proposes is tied back to a playbook clause.",
      },
      {
        title: "The reviewer stays in charge",
        body: "Risk scores, summaries, and redlines are recommendations. ClauseArk surfaces what changed and why; the lawyer, project exec, or producer decides what to accept.",
      },
      {
        title: "Make the lifecycle one place",
        body: "Intake, review, negotiation, signature, and renewal should live on the same record — not split across email, drives, and a separate sign tool.",
      },
    ],
  },
  finalCta: {
    headline: "See ClauseArk on your own contracts.",
    sub: "Bring one or two real contracts and we'll walk through review, redlines, and the lifecycle live in 30 minutes.",
    primary: { label: "Book a demo", path: "/demo" },
    secondary: { label: "Talk to sales", path: "/company/contact" },
  },
} as const;

/* ──────────────────────────────────────────────────────────────────
 * Platform pages
 * ────────────────────────────────────────────────────────────────── */

export const PLATFORM_PAGES = {
  "contract-intelligence": {
    eyebrow: "Platform · Contract Intelligence",
    title: "Playbook-based contract review, in plain English.",
    subtitle:
      "Upload a contract, choose a playbook, and ClauseArk gives you per-clause risk, an overall risk score, an executive summary, and jurisdiction-aware notes — in six languages.",
    seo: {
      title: "Contract Intelligence · AI Review & Risk Scoring | ClauseArk",
      description:
        "Playbook-based AI review for PDFs and DOCX (incl. OCR). Per-clause risk levels, an overall risk score, executive summaries, and jurisdiction-aware notes in six languages.",
    },
    relatedLink: {
      label: "Read why our AI is different — BYOK, citations, audit trail",
      path: "/platform/ai",
    },
    capabilities: [
      {
        title: "PDF and DOCX intake — with OCR",
        body:
          "Upload single contracts or bulk-upload from your computer. Scanned PDFs are OCR'd automatically so old paper isn't off-limits.",
      },
      {
        title: "Bulk import from your DMS",
        body:
          "Pull contracts straight from SharePoint, Box, Google Drive, iManage, or NetDocuments — no manual download/upload step.",
      },
      {
        title: "Playbook-based clause review",
        body:
          "Each clause is checked against the playbook you choose for the deal type. ClauseArk flags what's standard, what's a fallback, and what's off-policy.",
      },
      {
        title: "Risk levels and an overall score",
        body:
          "Every clause gets a risk level. Every contract gets a single risk score so reviewers know where to spend their time first.",
      },
      {
        title: "Executive summary",
        body:
          "A short, non-lawyer-readable summary of the deal: parties, key economics, term, renewal, liabilities, and the issues worth escalating.",
      },
      {
        title: "Jurisdiction-aware notes",
        body:
          "Where the contract's governing law matters, ClauseArk surfaces jurisdiction-specific notes so reviewers don't miss a state- or country-specific issue.",
      },
      {
        title: "Six-language review",
        body:
          "Run review in English, Spanish, French, German, Japanese, or Brazilian Portuguese — on the same playbook configuration.",
      },
      {
        title: "Industry packs",
        body:
          "Turn on the Construction or Insurance pack to add industry-specific contract types and playbooks on top of the General default.",
      },
      {
        title: "Weekly playbook drift detection",
        body:
          "ClauseArk watches what reviewers actually accept on executed contracts and emails playbook owners each week when a clause keeps drifting away from the preferred wording.",
      },
      {
        title: "Multi-document deal review",
        body:
          "Group an MSA, SOW, DPA, and order form as one Deal Bundle and ClauseArk reviews them together — flagging counterparty mismatches, governing-law conflicts, divergent insurance and payment terms, missing DPAs, and definitions that drift between documents.",
      },
      {
        title: "Live negotiation copilot",
        body:
          "On a counter-party call? Open the live copilot inside the contract: it listens through your browser, follows which clause you're discussing, and surfaces your playbook's preferred and fallback wording — plus the closest precedent from your executed contracts — without leaving the page. Capture agreed-language as a draft in one click and the audit trail records the session against the contract.",
      },
      {
        title: "Portfolio-wide conflict checks",
        body:
          "When a new contract enters review, ClauseArk cross-checks it against every executed contract in your repository — surfacing MFN re-pricing risk, conflicting exclusivity, audit-rights overlap, governing-law mismatches with the same counterparty group, currency mismatches with a parent MSA, term-overlap on the same subject, and material liability-cap inconsistencies — with a one-paragraph plain-English business-impact note for each. Reviewers can dismiss with a reason, and every dismissal lands on the contract's audit trail.",
      },
      {
        title: "Regulation watcher",
        body:
          "ClauseArk monitors the regulators that matter to your vertical and, the moment a new rule lands, scans your repository, flags every contract it touches, and drafts a Word amendment ready for counterparty signature.",
      },
      {
        title: "Trojan-Horse Detector — obligations hiding in the wrong places",
        body:
          "Counterparties love to bury indemnities in a definition, a fee escalator in Schedule B, or an audit-rights ratchet behind \"in accordance with ISO 27001.\" ClauseArk segments every contract into definitions, recitals, exhibits, schedules, footnotes, cross-references, and any URLs incorporated by reference, then surfaces every obligation lurking outside the body clauses with a plain-English explanation, a suggested pushback line, and a one-click jump from the Word add-in. Tenants can opt into fetching incorporated-by-reference URLs so reviewers see what they're actually agreeing to without leaving the app.",
      },
      {
        title: "Negotiation simulator",
        body:
          "Type \"what if I hold this position?\" on any clause and the simulator returns your tenant's historical close rate, average days to sign, and average rounds across this counterparty, this industry, your whole portfolio, and this contract type. Side-by-side compare against an alternate position. Numbers only when ClauseArk has at least 5 matching deals — no LLM guesses, no cross-tenant leakage.",
      },
    ],
  },
  "chat-approvals": {
    eyebrow: "Platform · Approve in Chat",
    title: "Approve, reject, or comment on contracts without leaving Slack or Teams.",
    subtitle:
      "When a contract hits the approval step, ClauseArk posts an interactive message to the right Slack or Microsoft Teams channel. In Slack, Approve / Reject / Comment all happen in chat. In Teams, Comment happens in chat and Approve / Reject open ClauseArk in the browser so the decision is recorded under the user's verified SSO identity.",
    seo: {
      title: "Slack & Microsoft Teams Contract Approvals | ClauseArk",
      description:
        "Interactive Approve / Reject / Comment buttons in Slack and a Comment + verified-identity Approve/Reject deep-link in Microsoft Teams. Identity mapping, account linking, role + assignee authorization gate, and a hash-chained audit log entry for every chat decision.",
    },
    capabilities: [
      {
        title: "Slack: in-channel buttons + reason modal for every action",
        body:
          "Each Slack approval message includes Approve, Reject, Comment, and Open in ClauseArk buttons. All three of Approve, Reject, and Comment open a Slack modal that asks for an optional free-text reason, so the audit trail captures *why*, not just *what*. Slack identity is verified through OAuth + users.info before the modal opens.",
      },
      {
        title: "Teams: Comment in chat, Approve / Reject in the web app",
        body:
          "Teams MessageCards posted via incoming webhooks don't carry a verified user identity, so Approve and Reject in Teams are deep-links into ClauseArk where the user is signed in through SSO and the existing contract authorization applies. Only Comment runs through the in-channel webhook — it's non-binding and never advances workflow state on its own.",
      },
      {
        title: "Posted to the channel you choose",
        body:
          "Per-tenant defaults for Requests, Approvals, and Renewals. Pick the Slack channel or Teams channel from the connector setup screen — no per-contract routing needed.",
      },
      {
        title: "Identity mapping with DM-based account linking",
        body:
          "Slack clicks are resolved to a ClauseArk user via the Slack profile email. If we can't match, the bot opens a private DM explaining how to link — no silent failures. Teams comments include an attribution email that's recorded in the audit log.",
      },
      {
        title: "Role + assignee authorization gate",
        body:
          "Approve and Reject decisions are only accepted from a privileged role (admin, partner, associate, paralegal) or the assignee on an active approval task on the workflow — the same rule the web UI enforces. Denied attempts are written to the audit log too.",
      },
      {
        title: "Workflow-state permission gate",
        body:
          "The decision is only applied when the workflow is in the Approval step. Late clicks (already approved, cancelled, or moved on) are rejected with a clear message, not silently swallowed.",
      },
      {
        title: "Hash-chained audit log entry per click",
        body:
          "Every chat decision writes a row to the per-tenant audit log capturing source channel, message reference, the resolved actor (user id, email, display name, role), and the decision. The same chain that records every API call also records every chat click.",
      },
      {
        title: "Signed webhook verification",
        body:
          "Slack interactivity is verified with the Slack signing secret per request. Teams uses the actionable-message HMAC. Replay-safe with a 5-minute timestamp window.",
      },
    ],
  },
  "workflow-automation": {
    eyebrow: "Platform · Workflow & Lifecycle",
    title: "Matters, redlines, renewals — one timeline.",
    subtitle:
      "Group contracts into matters. Take work in through public intake forms or an intake email. Run negotiation rounds. Build approval workflows. Watch renewals on a single dashboard.",
    seo: {
      title: "Contract Workflow & Lifecycle | ClauseArk",
      description:
        "Matters, public intake forms and email, AI redlines, negotiation rounds, workflow builder with tasks, and a renewals dashboard — for contract teams in Legal, Construction, and Insurance.",
    },
    capabilities: [
      {
        title: "Matters",
        body:
          "Group related contracts under a matter so the deal, the M&A, or the vendor relationship has one record instead of fifteen email threads.",
      },
      {
        title: "Public intake forms",
        body:
          "Stand up a self-serve intake form so sales, procurement, or HR can request a contract without pinging legal in Slack.",
      },
      {
        title: "Intake email address",
        body:
          "Forward counterparty paper to a dedicated address and it lands in the queue with the contract type pre-detected.",
      },
      {
        title: "Email + Slack native intake agent",
        body:
          "Forward a deal email or run /clauseark intake in Slack and the agent extracts the deal facts, opens or finds the matter, drafts the first version from your template, assigns reviewers, and posts a 'what I did' summary back to the original thread — with one-click clarify, undo, and redo.",
      },
      {
        title: "AI redlines tied to your playbook",
        body:
          "Every suggested redline points back to the playbook position it implements, plus up to three executed-precedent clauses you've already negotiated — so reviewers can defend each change line by line. Accept, edit, or reject without leaving the contract.",
      },
      {
        title: "Negotiation rounds",
        body:
          "Import the counterparty's revised .docx. ClauseArk matches their changes back to the affected clauses and tracks every round in one place.",
      },
      {
        title: "Per-clause history",
        body:
          "See how each clause moved across drafts and rounds — what your team proposed, what the counterparty pushed back on, and what was finally agreed.",
      },
      {
        title: "Workflow builder with tasks",
        body:
          "Build approval flows with the tasks each step needs. Route reviewers, assign approvers, and track who owes what.",
      },
      {
        title: "Approve in Slack or Microsoft Teams",
        body:
          "When a contract hits the approval step, ClauseArk posts an interactive message to the channel you choose. Approvers click Approve, Reject, or Comment in chat — every click lands on the contract's audit log.",
      },
      {
        title: "Renewals dashboard",
        body:
          "All upcoming renewals and termination windows on one screen, sorted by what's coming up next.",
      },
      {
        title: "Autonomous renewal agent",
        body:
          "Ninety days before each renewal, ClauseArk's agent gathers comparable past renewals, summarises your leverage, drafts a strategy memo, and prepares a redlined draft. The approval and signature steps always pause for a human — every AI call is capped per run and every step writes to the hash-chained audit log.",
      },
    ],
  },
  "compliance-engine": {
    eyebrow: "Platform · Trust & Access",
    title: "SSO, BYOK, and an audit log of every action.",
    subtitle:
      "ClauseArk gives security and IT the controls they need to approve a contract platform — without locking the rest of the business out.",
    seo: {
      title: "Trust & Access · SSO, BYOK, Audit Logs | ClauseArk",
      description:
        "Single sign-on via WorkOS (SAML and OIDC), customer-managed encryption keys via AWS KMS or Azure Key Vault, and a per-tenant audit log of every action.",
    },
    capabilities: [
      {
        title: "Single sign-on via WorkOS",
        body:
          "SAML and OIDC SSO through WorkOS — connect Okta, Microsoft Entra ID, Google Workspace, JumpCloud, OneLogin, and more.",
      },
      {
        title: "Bring-your-own-key encryption",
        body:
          "Customer-managed keys via AWS KMS or Azure Key Vault. Rotate or revoke on your schedule.",
      },
      {
        title: "Audit log",
        body:
          "Every meaningful action in your tenant — who uploaded a contract, who ran review, who signed — is recorded in the audit log.",
      },
      {
        title: "Roles and access controls",
        body:
          "Give the right people the right level of access. Reviewers, approvers, and admins each see what they need and not more.",
      },
    ],
  },
  integrations: {
    eyebrow: "Platform · Integrations",
    title: "Plays nicely with the tools you already use.",
    subtitle:
      "Native integrations across signing, CRM, identity, and document storage. The list below is what's shipped today.",
    seo: {
      title: "Integrations · DocuSign, Salesforce, HubSpot, SharePoint | ClauseArk",
      description:
        "Native integrations with DocuSign, Adobe Sign, Dropbox Sign, Salesforce, HubSpot, SharePoint, Box, Google Drive, iManage, NetDocuments, and SSO via WorkOS.",
    },
    categories: [
      {
        title: "E-signature",
        items: ["DocuSign", "Adobe Sign", "Dropbox Sign", "Native ClauseArk Sign"],
      },
      {
        title: "CRM",
        items: ["Salesforce", "HubSpot", "Pipedrive", "Microsoft Dynamics 365"],
      },
      {
        title: "ERP & procurement",
        items: ["NetSuite", "SAP Ariba", "Workday", "Coupa"],
      },
      {
        title: "Document storage & DMS",
        items: ["SharePoint", "Box", "Google Drive", "iManage", "NetDocuments (bulk import; signed-contract mirror writeback to your DMS folder)"],
      },
      {
        title: "Email intake",
        items: ["Outlook & Gmail mailbox scanner (counterparty paper auto-routes to the queue with contract type pre-detected)"],
      },
      {
        title: "Chat approvals",
        items: [
          "Slack (interactive Approve / Reject / Comment in channel + DM account linking)",
          "Microsoft Teams (Comment in-channel + verified-identity Approve/Reject deep-link)",
        ],
        cta: { label: "See how chat approvals work", path: "/platform/chat-approvals" },
      },
      {
        title: "Notifications",
        items: ["Slack & Teams (review, approval, and signature events)"],
      },
      {
        title: "Outbound webhooks",
        items: [
          "Push obligations and key dates to Jira, NetSuite, ServiceNow, or any HTTPS endpoint — HMAC-signed, retried for up to 7 days, with a per-endpoint dead-letter view and one-click recipes.",
        ],
      },
      {
        title: "Identity & SSO (via WorkOS)",
        items: ["Okta", "Microsoft Entra ID", "Google Workspace", "JumpCloud", "OneLogin", "Generic SAML / OIDC"],
      },
      {
        title: "Encryption keys (BYOK)",
        items: ["AWS KMS", "Azure Key Vault"],
      },
      {
        title: "Microsoft Word",
        items: ["ClauseArk Word add-in (in-Word AI review, redlines, clause library)"],
        cta: { label: "See the Word add-in", path: "/word-addin" },
      },
    ],
  },
  "ai-copilot": {
    eyebrow: "Platform · AI Copilot",
    title: "Chat with your contracts.",
    subtitle:
      "Ask the copilot anything about the contracts in your workspace and get a cited answer in seconds — across one contract or your entire repository.",
    seo: {
      title: "AI Copilot · Chat with Your Contracts | ClauseArk",
      description:
        "ClauseArk's AI copilot lets in-house legal, procurement, and operations ask plain-English questions across every contract in their workspace, with click-through citations and per-tenant guardrails.",
    },
    capabilities: [
      {
        title: "Plain-English questions, cited answers",
        body:
          "Ask \"which MSAs auto-renew this quarter?\" or \"summarize the indemnity in the Acme deal\" — every answer cites the contracts and clauses it pulled from, so reviewers can click through to the source.",
      },
      {
        title: "Workspace-wide or scoped to one contract",
        body:
          "Open the copilot from the sidebar to query the whole repository, or open it from a contract page to scope the conversation to that single deal.",
      },
      {
        title: "Hybrid semantic + keyword search",
        body:
          "The copilot uses embedding similarity to find clauses by meaning and falls back to lexical search so nothing's missed when a topic isn't yet indexed.",
      },
      {
        title: "Per-tenant data isolation",
        body:
          "The copilot can only see contracts in your workspace. Every tool call is scoped by tenant id at the database level — there is no cross-tenant fallback.",
      },
      {
        title: "Conversation history",
        body:
          "Sessions are saved, searchable, and renameable. Pick up a chat from yesterday or share a summary with a teammate.",
      },
      {
        title: "Audit log of every prompt and answer",
        body:
          "Every question and answer — and every tool call the copilot made — is recorded in the per-tenant audit log so security and compliance can review usage at any time.",
      },
      {
        title: "Bring-your-own model",
        body:
          "Runs against the AI provider you've chosen for your workspace — OpenAI or Anthropic — using the same model selection as the rest of ClauseArk.",
      },
      {
        title: "Fair-use limits, no surprise bills",
        body:
          "The Startup plan includes a generous monthly question allowance; Growth and Enterprise are fair-use unlimited. Per-tenant token caps protect against runaway costs.",
      },
    ],
  },
} as const;

/* ──────────────────────────────────────────────────────────────────
 * Solutions pages
 * ────────────────────────────────────────────────────────────────── */

/**
 * Industry Solutions pages share a parallel structure so no industry
 * looks like an afterthought:
 *   - hero (eyebrow / title / subtitle / seo)
 *   - pains       — what hurts today
 *   - contracts   — the contract types ClauseArk knows by name
 *   - playbooks   — the side-aware playbooks reviewers actually use
 *   - outcomes    — four stat tiles that summarize the difference
 *   - whatYouGet  — the five-bullet checklist of shipped capabilities
 *   - cta         — explicit call-out at the bottom of the page
 *
 * `enterprise` is intentionally lighter — it's a buyer shape (security
 * & SSO), not an industry, so it carries hero / pains / whatYouGet only.
 * The SolutionsPage component renders any optional sections that exist
 * and skips ones that don't, so both shapes live happily side by side.
 */
export const SOLUTIONS_PAGES = {
  "in-house-legal": {
    industry: "Legal",
    eyebrow: "Solutions · Legal · In-House",
    title: "Run the contract lifecycle without growing the team.",
    subtitle:
      "Self-serve intake, playbook-based review, and a renewals dashboard so a small in-house legal team can support a much larger business.",
    seo: {
      title: "Contract Lifecycle for In-House Legal Teams | ClauseArk",
      description:
        "Public intake forms, playbook-based review, AI redlines, negotiation rounds, workflows, and a renewals dashboard for in-house legal teams.",
    },
    pains: [
      {
        title: "Sales asks for NDAs at 5pm Friday",
        body: "Without a self-serve path, every standard request becomes a manual review queue.",
      },
      {
        title: "Renewals slip through the cracks",
        body: "Termination windows live in PDFs no one re-reads. Spreadsheets and calendar reminders eventually fail.",
      },
      {
        title: "Inbound and outbound paper diverge",
        body: "Procurement and Sales contracts use different playbooks, with no central view of what's where.",
      },
    ],
    contracts: {
      headline: "Contracts your in-house team sees every week",
      sub: "Each one ships with the playbook fields, party detection, and renewal/termination extraction tuned to that contract type — not a generic NDA template.",
      items: [
        { name: "NDAs (mutual + one-way)", body: "Inbound and outbound, with party detection and term extraction so the executive summary actually has the parties and dates right." },
        { name: "MSAs and order forms", body: "Reviewed against your fallbacks, not a generic style guide. AI redlines map back to the specific playbook position they implement." },
        { name: "DPAs and BAAs", body: "Sub-processor lists, transfer mechanisms, and breach notice windows pulled into structured fields you can filter on." },
        { name: "Vendor and procurement paper", body: "Counterparty paper, your positions — side-aware redline so reviewers see what to push back on first." },
        { name: "Sales and partnership agreements", body: "Outbound paper from your template; inbound from the customer's. Same review, same risk score, same playbook." },
        { name: "Employment and offer letters", body: "Standard offer letters, separation agreements, and consulting agreements with the HR-side playbook in mind." },
      ],
    },
    playbooks: {
      headline: "Playbooks tuned to how an in-house team actually buys and sells",
      sub: "Different counterparties, different positions. Pick a side per matter — the same contract reviewed under a different playbook gives different recommendations.",
      items: [
        { name: "Vendor inbound", body: "Your fallbacks for inbound vendor paper — caps, indemnities, audit rights, data handling, and the redlines you've already negotiated." },
        { name: "Sales outbound", body: "Your standard sales paper, with the fallback positions sales is allowed to give without escalating to legal." },
        { name: "Procurement", body: "Procurement-side playbook for ERP, SaaS, and services paper — including pricing, term length, and exit." },
        { name: "Privacy / DPA", body: "GDPR, CCPA, and HIPAA-flavored positions for DPAs and BAAs, with sub-processor and SCC handling baked in." },
      ],
    },
    outcomes: [
      { stat: "1st pass", label: "AI review on inbound NDAs and MSAs in minutes, not days" },
      { stat: "Self-serve", label: "Public intake form + intake email — sales, HR, and procurement file their own" },
      { stat: "All renewals", label: "On one dashboard so nothing auto-renews silently" },
      { stat: "AI redlines", label: "Tied to your playbook positions, not a generic style guide" },
    ],
    whatYouGet: [
      "Public intake form and an intake email so the business can submit requests without pinging legal directly",
      "Playbook-based review with per-clause risk, an overall risk score, and an executive summary you can forward",
      "AI redlines tied to your playbook positions, plus negotiation rounds with counterparty .docx imports",
      "A renewals dashboard so nothing auto-renews silently — plus a Monday-morning Slack triage of vendor renewals in the next 60 days, grouped by recommendation (let lapse, renew as-is, renegotiate price, renegotiate terms) with one-click Approve / Renegotiate buttons and a 24-hour undo",
      "DocuSign, Adobe Sign, Dropbox Sign, or native signing — your choice",
      "Deal Bundles that review the MSA, SOW, DPA, and order form together so cross-document conflicts surface before signature",
    ],
    cta: {
      heading: "See ClauseArk on your own NDAs and MSAs.",
      body: "Bring one or two real inbound contracts and we'll walk through review, redlines, and the renewals dashboard live in 30 minutes.",
      primary: { label: "Book a demo", path: "/demo" },
      secondary: { label: "See platform pricing", path: "/pricing" },
    },
  },
  "law-firms": {
    industry: "Legal",
    eyebrow: "Solutions · Legal · Law Firms",
    title: "Stop spending senior time on first-pass review.",
    subtitle:
      "Playbook-based review and AI redlines for routine paper, so your lawyers focus on the issues that actually need them.",
    seo: {
      title: "Contract Review Software for Law Firms | ClauseArk",
      description:
        "Playbook-based AI review, suggested redlines, and matter-level organization for law firms working through high volumes of routine contracts.",
    },
    pains: [
      {
        title: "First-pass review eats junior time",
        body: "Repetitive NDA and MSA review consumes capacity that should be on substantive work.",
      },
      {
        title: "No central clause memory across matters",
        body: "Every matter starts from a search of someone's Outlook instead of a shared clause library.",
      },
      {
        title: "Counterparty turns are still manual",
        body: "Importing redlines and matching them back to your prior turn is hand-work that adds up across files.",
      },
    ],
    contracts: {
      headline: "Routine paper your firm sees across matters",
      sub: "The contract types that eat the most associate time — handled with a playbook, a risk score, and AI redlines tied to clauses you've already negotiated.",
      items: [
        { name: "NDAs (mutual + one-way)", body: "M&A, financing, and commercial NDAs reviewed against the firm or client playbook with party detection on inbound paper." },
        { name: "MSAs and SOWs", body: "Vendor inbound and customer outbound paper, with side-aware AI redlines and per-clause risk." },
        { name: "M&A deal NDAs and side letters", body: "Higher-stakes NDAs and side letters with custom standstill, non-solicit, and exclusivity positions per client." },
        { name: "Employment, separation, and equity paper", body: "Repeat-pattern employment and separation agreements that don't need a senior associate to triage." },
        { name: "Settlement and release agreements", body: "Routine releases reviewed against firm-standard positions before partner sign-off." },
        { name: "Procurement and vendor agreements", body: "Inbound vendor paper for both the firm itself and clients running procurement." },
      ],
    },
    playbooks: {
      headline: "Firm-standard and client-specific playbooks",
      sub: "Run the same NDA against the firm playbook for one matter and the client's playbook for another — same product, different recommendations.",
      items: [
        { name: "Firm-standard playbook", body: "Your firm's positions on NDAs, MSAs, and the routine paper your associates handle every week." },
        { name: "Client-specific playbooks", body: "One per client where it matters — fallbacks, must-haves, and red lines that map to that client's risk tolerance." },
        { name: "Inbound vs. outbound side-aware", body: "Pick a side per matter; the same playbook gives different recommendations depending on whose paper it is." },
        { name: "Six-language review", body: "English, Spanish, French, German, Japanese, and Brazilian Portuguese — same playbook, same risk model." },
      ],
    },
    outcomes: [
      { stat: "Junior time", label: "Off repetitive first-pass review and onto substantive work" },
      { stat: "Matter-level", label: "Every contract under one shared record across the team" },
      { stat: "Counterparty turns", label: ".docx import auto-matched back to your prior clauses" },
      { stat: "6 languages", label: "Same playbook, six review languages out of the box" },
    ],
    whatYouGet: [
      "Per-clause risk and an overall risk score on every contract uploaded",
      "AI redlines tied to firm or client playbook positions",
      "Counterparty .docx import that matches their changes back to your clauses",
      "Matters to organize related contracts under one record",
      "Review in English, Spanish, French, German, Japanese, or Brazilian Portuguese",
    ],
    cta: {
      heading: "See ClauseArk on the routine paper your associates handle.",
      body: "Bring a real NDA or MSA and we'll show first-pass review, AI redlines, and a counterparty turn live in 30 minutes.",
      primary: { label: "Book a demo", path: "/demo" },
      secondary: { label: "See platform pricing", path: "/pricing" },
    },
  },
  enterprise: {
    industry: "Buyer shape",
    eyebrow: "Solutions · Enterprise (security & SSO)",
    title: "Controls security teams expect, without slowing the business down.",
    subtitle:
      "SSO via WorkOS, customer-managed encryption keys, and a per-tenant audit log of every action — on top of the same review and lifecycle every team uses, in any industry.",
    seo: {
      title: "Enterprise Contract Lifecycle Management | ClauseArk",
      description:
        "SSO via WorkOS (SAML/OIDC), BYOK encryption with AWS KMS or Azure Key Vault, and a full audit log — for enterprise security and IT teams approving a contract platform.",
    },
    pains: [
      {
        title: "Security review blocks adoption",
        body: "Without SSO, BYOK, and an audit log, your IT and security partners can't approve a new contract platform.",
      },
      {
        title: "Reviews and approvals scatter across teams",
        body: "Different business units use different processes, so leadership has no consistent view of what's in flight.",
      },
      {
        title: "Renewals are someone else's problem — until they aren't",
        body: "Auto-renewals fire because no one owns the dashboard. Finance finds out after the fact.",
      },
    ],
    whatYouGet: [
      "SSO via WorkOS (SAML and OIDC) — Okta, Microsoft Entra ID, Google Workspace, and more",
      "Bring-your-own-key encryption with AWS KMS or Azure Key Vault",
      "Per-tenant audit log of every meaningful action",
      "Workflow builder with tasks so business units can keep their own approval flows",
      "Renewals dashboard with a single view across the tenant",
    ],
    cta: {
      heading: "Need SSO, BYOK, and a full audit log?",
      body: "Talk to us about an Enterprise tenant — same product, security-led configuration. Works on top of any of the three industry packs.",
      primary: { label: "Talk to sales", path: "/company/contact" },
      secondary: { label: "See platform pricing", path: "/pricing" },
    },
  },
  construction: {
    industry: "Construction",
    eyebrow: "Solutions · Construction",
    title: "AIA, ConsensusDocs, and flow-down — built for GCs, subs, and owners.",
    subtitle:
      "Turn on the Construction pack and your tenant gets construction-specific contract types and playbooks. AIA A201/A401/A101 and ConsensusDocs 200/750 are pre-loaded; flow-down checker compares prime to subcontract; jurisdiction notes cover state lien and notice rules.",
    seo: {
      title: "Contract Software for Construction Legal Teams | ClauseArk",
      description:
        "ClauseArk's Construction pack: AIA and ConsensusDocs contract types, GC / sub / owner playbooks, flow-down clause checker (prime → sub), and state-specific lien and notice jurisdiction notes — for in-house construction legal, GCs, subs, and owners.",
    },
    pains: [
      {
        title: "General-purpose CLMs miss construction patterns",
        body: "Owner, GC, and subcontractor paper differ in dozens of ways. Generic playbooks miss the issues your team actually cares about — flow-down, indemnity tiers, lien waivers, T&M caps.",
      },
      {
        title: "Volume of subcontracts and change orders",
        body: "The work isn't one big contract — it's hundreds of subs, change orders, and amendments per project that need consistent treatment without grinding the legal queue to a halt.",
      },
      {
        title: "Flow-down errors land on the prime",
        body: "If a clause from the prime doesn't make it into the sub — or a sub-only clause sneaks back up — the GC eats the gap. Manually checking flow-down across hundreds of subs is where mistakes hide.",
      },
      {
        title: "State lien and notice rules differ in every jurisdiction",
        body: "Mechanic's lien deadlines, preliminary notice requirements, and pay-when-paid enforceability differ state by state. Missing a notice window is a hard dollar loss.",
      },
    ],
    contracts: {
      headline: "Contracts construction teams see every week",
      sub: "Industry contract types pre-loaded with construction-specific party detection (Owner / GC / Sub / Architect), term extraction, and the playbook fields project legal actually files on.",
      items: [
        { name: "AIA A101 / A201 / A401", body: "Owner-Contractor agreement, the General Conditions, and the Contractor-Subcontractor agreement — pre-loaded with the standard playbook." },
        { name: "ConsensusDocs 200 and 750", body: "Owner-CM and Subcontractor agreements with ConsensusDocs-specific party detection and clause structure." },
        { name: "Owner / custom prime contracts", body: "Owner paper that isn't AIA — reviewed against the playbook your team has already negotiated for that owner." },
        { name: "Subcontracts (trade and design-build)", body: "Bulk subcontract review with the flow-down checker comparing each sub back to the prime it sits under." },
        { name: "Change orders and amendments", body: "Change orders, T&M directives, and amendments handled as their own contract types — counted separately, reviewed the same way." },
        { name: "COIs, lien waivers, T&M agreements", body: "Insurance certificates, conditional and unconditional lien waivers, and time-and-materials agreements with state-specific jurisdiction notes." },
      ],
    },
    playbooks: {
      headline: "Side-aware playbooks for prime, sub, and owner",
      sub: "The same AIA A201 reviewed under a GC-prime playbook gives different recommendations than under a subcontractor-inbound playbook. Pick a side per matter.",
      items: [
        { name: "GC-prime", body: "GC-as-prime playbook for owner paper — caps, schedule, indemnity, payment, and termination positions you've already negotiated." },
        { name: "Subcontractor inbound", body: "Sub-side review of inbound prime paper — flow-down obligations you can live with vs. ones you have to push back on." },
        { name: "Owner-side", body: "Owner-side review of GC paper, with builder-risk, payment-and-performance bond, and lien-waiver positions." },
        { name: "Change-order playbook", body: "Change order and amendment positions tuned to the prime contract they sit under, with T&M caps and approval flow." },
      ],
    },
    outcomes: [
      { stat: "AIA + ConsensusDocs", label: "Pre-loaded contract types — A101 / A201 / A401, 200 / 750" },
      { stat: "Flow-down checker", label: "Prime → sub clause parity surfaced before you sign" },
      { stat: "State lien rules", label: "Mechanic's lien deadlines and notice windows by jurisdiction" },
      { stat: "Subs at scale", label: "Bulk subcontract import with per-clause history per sub" },
    ],
    whatYouGet: [
      "AIA A101 / A201 / A401 and ConsensusDocs 200 / 750 contract types pre-loaded by the Construction pack",
      "GC-prime, subcontractor-inbound, and owner-side playbooks with side-aware AI redlines",
      "Flow-down clause checker that compares prime contracts to subcontracts and surfaces gaps",
      "State-specific jurisdiction notes for mechanic's lien deadlines, notice windows, and pay-when-paid enforceability",
      "Regulation watcher tuned to AIA / ConsensusDocs revisions and state lien-law changes — every affected prime, sub, and change order surfaced with a draft amendment",
      "Bulk import from SharePoint, Box, Google Drive, iManage, and NetDocuments for whole-project intake",
    ],
    cta: {
      heading: "Run the Construction pack on your own paper.",
      body: "Bring a prime AIA A201 and a couple of subs — we'll show flow-down, jurisdiction notes, and AI redlines live in 30 minutes. Or start a 14-day trial.",
      primary: { label: "Book a demo", path: "/demo" },
      secondary: { label: "See Construction pricing", path: "/pricing/construction" },
    },
  },
  insurance: {
    industry: "Insurance",
    eyebrow: "Solutions · Insurance",
    title: "Built for producer, MGA, TPA, and treaty paper.",
    subtitle:
      "Carrier, MGA, sub-producer, TPA, BAA, and reinsurance treaty playbooks. Binding-authority extractor surfaces every per-risk limit, premium cap, and claims-authority cliff. Year-over-year diff for renewal review. Surplus-lines and fiduciary-trust rules for CA, NY, TX, and FL.",
    seo: {
      title: "Insurance Contract Software for Carriers, MGAs, and TPAs | ClauseArk",
      description:
        "Producer / agency appointment, MGA program, sub-producer, TPA, BAA, and reinsurance treaty playbooks. Side-aware redline (Carrier / MGA / Sub-producer / TPA / Cedent / Reinsurer), binding-authority extraction, year-over-year renewal diff, and state-by-state surplus-lines & fiduciary-trust rules. Built for insurance legal and compliance teams.",
    },
    pains: [
      {
        title: "Binding authority is buried on Schedule C",
        body: "Per-risk limit, aggregate cap, premium volume cap, claims authority — every renewal hides a different combination. Missing one drives a sub-limit breach the carrier eats.",
      },
      {
        title: "Year-over-year drift is invisible",
        body: "Treaty year 2026 quietly drops the 25% quota share to 20% and adds a 60% loss-ratio profit-commission cliff. Without a structured diff, nobody catches it before binding.",
      },
      {
        title: "Surplus lines is jurisdiction-by-jurisdiction",
        body: "California 3.0% + 0.25%, NY 3.6% + 0.18% via ELANY, Texas 4.85% + 0.075% via SLSOT, Florida 4.94% + 0.06% via FSLSO. Diligent-effort and stamping rules differ in every state.",
      },
      {
        title: "Premium-trust failures are existential",
        body: "Commingling premium funds is grounds for licence revocation in every state. Producer, MGA, and sub-producer agreements need explicit fiduciary-account language and audit rights.",
      },
    ],
    contracts: {
      headline: "The insurance contracts ClauseArk knows by name",
      sub: "Industry contract types pre-loaded with insurance-specific party detection (Carrier / MGA / Sub-producer / TPA / Cedent / Reinsurer), Schedule C parsing, and the playbook fields compliance actually files on.",
      items: [
        { name: "Producer / agency appointment", body: "Producer agreements with commission, reporting cadence, fiduciary-trust, and termination tail handled as structured fields." },
        { name: "MGA program agreement", body: "MGA program paper with binding authority, claims authority, premium volume caps, and audit rights extracted off Schedule C." },
        { name: "Sub-producer agreements", body: "Sub-producer paper that flows down from the producer or MGA agreement above it — with a flow-down view across the chain." },
        { name: "TPA agreements", body: "Third-party administrator agreements with claims authority, settlement caps, and reporting handled as first-class fields." },
        { name: "BAA / DPA", body: "HIPAA-flavored BAAs and privacy DPAs reviewed against the carrier-side playbook, not a generic privacy template." },
        { name: "Reinsurance treaty", body: "Quota share, surplus, and excess-of-loss treaty paper with quota-share %, attachment, profit commission, and loss-ratio cliffs extracted." },
        { name: "Primary policies and endorsements", body: "Primary policies, endorsements, and manuscript wordings with E&O risk framing on every clause." },
      ],
    },
    playbooks: {
      headline: "Side-aware playbooks across the insurance chain",
      sub: "The same producer agreement reviewed as Carrier vs. Sub-producer gives different recommendations. Pick a side per matter — six side-aware perspectives ship out of the box.",
      items: [
        { name: "Carrier", body: "Carrier-side review of producer, MGA, and TPA paper — your fallbacks for authority, audit, and termination." },
        { name: "MGA", body: "MGA-side review of carrier program agreements and sub-producer paper, with binding-authority preservation top of mind." },
        { name: "Sub-producer", body: "Sub-producer-side review of inbound producer or MGA paper — what you can live with on commission, accounting, and termination." },
        { name: "TPA", body: "TPA-side playbook for claims authority, settlement caps, and reporting cadence positions." },
        { name: "Cedent / Reinsurer", body: "Treaty paper reviewed from either side, with quota share, attachment, and profit-commission cliffs in structured form." },
      ],
    },
    outcomes: [
      { stat: "6 playbooks", label: "Producer, MGA, Sub-producer, TPA, BAA, and Treaty out of the box" },
      { stat: "Schedule C", label: "Binding-authority extraction with per-risk, per-occurrence, aggregate caps" },
      { stat: "YoY diff", label: "Renewal-blocking change detection with materiality scoring" },
      { stat: "4 states", label: "Surplus-lines, fiduciary-trust, and producer-licensing rules baked in" },
    ],
    whatYouGet: [
      "Six insurance playbooks: Producer / agency appointment, MGA program, sub-producer, TPA, BAA, and reinsurance treaty",
      "Binding-authority extractor that pulls per-risk, per-occurrence, aggregate, and premium-volume caps off Schedule C",
      "Year-over-year renewal diff with materiality scoring on every authority and limit change",
      "Side-aware redline for Carrier, MGA, Sub-producer, TPA, Cedent, and Reinsurer perspectives",
      "Surplus-lines, fiduciary-trust, and producer-licensing rules built in for CA, NY, TX, and FL",
      "Regulation watcher tuned to NAIC bulletins and ACORD form revisions — every affected producer agreement, MGA program, and treaty surfaced with a draft amendment",
      "COI auto-renewal agent — emails the broker on a configurable lead-time, parses the ACORD reply, and files the new certificate with a per-step audit trail and optional human-approval gate",
      "Binding-authority drift monitor — nightly scan that flags every executed contract whose lines of business, territory, or limits exceed what the producer was actually appointed to bind",
    ],
    cta: {
      heading: "Run the Insurance pack on your own producer or treaty paper.",
      body: "Bring a real producer agreement, an MGA program contract, or last year's treaty — we'll show binding-authority extraction and the YoY diff live. Or start a 14-day trial.",
      primary: { label: "Book a demo", path: "/demo" },
      secondary: { label: "See Insurance pricing", path: "/pricing/insurance" },
    },
  },
} as const;

/* ──────────────────────────────────────────────────────────────────
 * Pricing
 * ────────────────────────────────────────────────────────────────── */

export const PRICING = {
  eyebrow: "Pricing",
  title: "Plans for the team you have today.",
  subtitle:
    "Pay for the seats you use. All plans include playbook-based AI review, AI redlines, the matters and lifecycle workspace, and an audit log.",
  seo: {
    title: "Pricing · Horizontal CLM, Vertical-native, and Word Add-in | ClauseArk",
    description:
      "ClauseArk pricing for contract teams. Horizontal CLM tiers (Startup, Growth, Enterprise), vertical-native self-serve plans for Construction & Insurance (Solo $199, Team $499, Pro $1,200/mo + $29/contract), and a Word add-in for solo reviewers.",
  },
  plans: [
    {
      name: "Startup",
      price: "$0",
      cadence: "/ mo · up to 3 seats",
      tagline: "For pre-Series B teams getting their first paper signed.",
      cta: { label: "Start free", path: "/demo" },
      featured: false,
      features: [
        "Playbook-based AI review",
        "AI redlines tied to your playbook",
        "Matters, intake form, and intake email",
        "Native ClauseArk Sign",
        "Standard playbooks (NDA, MSA, DPA)",
        "AI Copilot — chat with your contracts (50 questions / mo)",
        "Email support",
      ],
    },
    {
      name: "Growth",
      price: "$800",
      cadence: "/ mo · 15 seats included",
      tagline: "For in-house teams running the full lifecycle.",
      cta: { label: "Book a demo", path: "/demo" },
      featured: true,
      features: [
        "Everything in Startup",
        "Custom playbooks and clause library",
        "Workflow builder with tasks",
        "Negotiation rounds with counterparty .docx import",
        "Renewals dashboard",
        "Post-signature obligation tracking with reminders + weekly digest",
        "DocuSign, Adobe Sign, and Dropbox Sign",
        "Salesforce and HubSpot integrations + embedded Salesforce Lightning panel",
        "Bulk import from SharePoint, Box, Google Drive, iManage, NetDocuments",
        "Multi-language review (EN, ES, FR, DE, JA, PT-BR)",
        "AI Copilot — chat with your contracts (fair-use unlimited)",
        "Custom Agents SDK — define multi-step contract automations in code",
      ],
    },
    {
      name: "Enterprise",
      price: "Custom",
      cadence: "Unlimited seats",
      tagline: "For teams that need SSO, BYOK, and per-tenant controls.",
      cta: { label: "Talk to sales", path: "/company/contact" },
      featured: false,
      features: [
        "Everything in Growth",
        "SSO via WorkOS (SAML and OIDC)",
        "Bring-your-own-key encryption (AWS KMS or Azure Key Vault)",
        "Per-tenant audit log",
        "Industry packs (Construction, Insurance)",
        "Dedicated customer success contact",
      ],
    },
  ],
  comparison: [
    {
      group: "Review",
      rows: [
        { feature: "Playbook-based AI review", values: ["✓", "✓", "✓"] },
        { feature: "AI redlines tied to playbook", values: ["✓", "✓", "✓"] },
        { feature: "Custom playbooks & clause library", values: ["–", "✓", "✓"] },
        { feature: "Multi-language review (6 languages)", values: ["–", "✓", "✓"] },
      ],
    },
    {
      group: "Lifecycle",
      rows: [
        { feature: "Matters, intake form, intake email", values: ["✓", "✓", "✓"] },
        { feature: "Workflow builder with tasks", values: ["–", "✓", "✓"] },
        { feature: "Negotiation rounds (counterparty .docx)", values: ["–", "✓", "✓"] },
        { feature: "Renewals dashboard", values: ["–", "✓", "✓"] },
        { feature: "Interactive chat approvals (Slack: Approve / Reject / Comment in-channel; Teams: Comment in-channel + verified-identity Approve/Reject deep-link)", values: ["–", "✓", "✓"] },
        { feature: "Post-signature obligation tracking", values: ["–", "✓", "✓"] },
        { feature: "Native ClauseArk Sign", values: ["✓", "✓", "✓"] },
        { feature: "DocuSign / Adobe Sign / Dropbox Sign", values: ["–", "✓", "✓"] },
      ],
    },
    {
      group: "AI Copilot",
      rows: [
        { feature: "Chat with your contracts", values: ["50 q/mo", "Fair-use", "Fair-use"] },
        { feature: "Per-contract chat (scoped)", values: ["✓", "✓", "✓"] },
        { feature: "Conversation history (search/rename)", values: ["✓", "✓", "✓"] },
        { feature: "Click-through citations to clauses", values: ["✓", "✓", "✓"] },
      ],
    },
    {
      group: "Trust & Access",
      rows: [
        { feature: "Audit log", values: ["✓", "✓", "✓"] },
        { feature: "SSO via WorkOS (SAML / OIDC)", values: ["–", "–", "✓"] },
        { feature: "BYOK encryption (AWS KMS / Azure KV)", values: ["–", "–", "✓"] },
        { feature: "Industry packs (Construction, Insurance)", values: ["–", "–", "✓"] },
      ],
    },
    {
      group: "Support",
      rows: [
        { feature: "Support channel", values: ["Email", "Email + chat", "Dedicated CS contact"] },
      ],
    },
  ],
  faqs: [
    {
      q: "Do I need to talk to sales to start?",
      a: "No. The Startup plan is self-serve, and so are all three vertical-native plans (Solo, Team, Pro) for Construction & Insurance. Growth and Enterprise on the horizontal track involve a 30-minute scoping call so we can map your playbook before you start.",
    },
    {
      q: "What's the difference between the horizontal and vertical-native tracks?",
      a: "The horizontal CLM tiers (Startup, Growth, Enterprise) are billed annually and built for any mid-market contract team that wants a procurement-grade platform — across Legal, Construction, Insurance, or General. The vertical-native plans (Solo $199, Team $499, Pro $1,200/mo) are monthly self-serve, ship with the construction or insurance playbook pre-installed, and add $29 / contract pay-as-you-go on top. Pick whichever matches how your team buys.",
    },
    {
      q: "How does the $29 / contract pay-as-you-go work on vertical-native plans?",
      a: "Each vertical-native tier includes an annual contract allowance (Solo: 25, Team: 100, Pro: 400 contracts/yr). Anything over the allowance is billed at $29 per contract reviewed — no annual commitment, no surprise overage tiers.",
    },
    {
      q: "Can I bring my own e-signature provider?",
      a: "Yes. Native ClauseArk Sign is included on every plan. DocuSign, Adobe Sign, and Dropbox Sign are first-class connectors on Growth and Enterprise.",
    },
    {
      q: "What languages does AI review support?",
      a: "English, Spanish, French, German, Japanese, and Brazilian Portuguese — on the same playbook configuration.",
    },
    {
      q: "How does intake work?",
      a: "You can post a public intake form, point a dedicated email address at ClauseArk, or upload contracts directly. Counterparty paper that arrives by email lands in the queue with the contract type pre-detected.",
    },
    {
      q: "How do industry packs work?",
      a: "Industry packs are tenant-level. Turn on Construction or Insurance and your tenant gets industry-specific contract types and playbooks on top of the General default.",
    },
    {
      q: "Can I import a counterparty's revised contract?",
      a: "Yes. Import their revised .docx and ClauseArk matches their changes back to the affected clauses, round after round.",
    },
  ],
} as const;

/* ──────────────────────────────────────────────────────────────────
 * Vertical-native Pricing (Construction & Insurance, self-serve)
 *
 * Source-of-truth seat caps and contract caps live in
 * `lib/db/src/schema/planLimits.ts` (`construction_*` / `insurance_*`):
 * Solo = 1 seat / 25 contracts/yr, Team = 3 seats / 100 contracts/yr,
 * Pro = 8 seats / 400 contracts/yr. Keep this block, the deck Pricing
 * slide (`artifacts/pitch-deck/src/pages/slides/Pricing.tsx`), and
 * `VERTICAL_PRICING` below in sync — if planLimits changes, update all
 * three.
 *
 * Track positioning: monthly self-serve, no annual commitment, no
 * procurement gate. Pay-as-you-go ($29/contract) layered on every tier.
 * ────────────────────────────────────────────────────────────────── */

export const VERTICAL_NATIVE_PRICING = {
  sectionId: "vertical-native",
  eyebrow: "Vertical-native · Self-serve",
  heading: "Built for Construction & Insurance teams that buy in minutes, not quarters.",
  subheading:
    "Monthly self-serve plans with the construction or insurance playbook pre-installed. No annual commitment. Cancel anytime.",
  payAsYouGoLine:
    "+ $29 / contract pay-as-you-go on every vertical-native tier — no annual commitment required.",
  signupDialog: {
    eyebrow: "Vertical-native · Self-serve signup",
    title: "Start your vertical-native workspace",
    description:
      "Drop your email and pick your industry. We'll provision your workspace with the right playbook pre-installed and email you the sign-in link.",
    emailLabel: "Email address",
    emailPlaceholder: "you@example.com",
    verticalLabel: "Industry",
    verticalOptions: [
      { value: "construction", label: "Construction" },
      { value: "insurance", label: "Insurance" },
    ],
    successTitle: "Your workspace is on the way",
    successBody:
      "Check your inbox in the next few minutes for your sign-in link. No procurement call required — pick up where you left off any time.",
    ctaLabel: "Start workspace",
    ctaLoading: "Submitting…",
    privacyNote: "Personal email is fine. By submitting, you agree to our Privacy Policy.",
  },
  plans: [
    {
      name: "Solo",
      price: "$199",
      cadence: "/ mo",
      billing: "Single user · monthly or annual",
      tagline: "For one attorney or contracts manager getting paper out the door.",
      cta: { label: "Get started" },
      featured: false,
      features: [
        "1 seat · 25 contracts / yr included",
        "Pre-built construction or insurance playbook",
        "Word add-in & e-sign",
        "AI review with per-clause risk and rationale",
        "Pay-as-you-go overage at $29 / contract",
      ],
      notIncluded: "",
    },
    {
      name: "Team",
      price: "$499",
      cadence: "/ mo",
      billing: "Up to 3 seats · monthly or annual",
      tagline: "For a contracts pod sharing one playbook and one workspace.",
      cta: { label: "Get started" },
      featured: true,
      features: [
        "3 seats · 100 contracts / yr included",
        "Shared playbook & clause library",
        "Portfolio search across executed contracts",
        "Negotiation rounds with counterparty .docx import",
        "Pay-as-you-go overage at $29 / contract",
      ],
      notIncluded: "",
    },
    {
      name: "Pro",
      price: "$1,200",
      cadence: "/ mo",
      billing: "Up to 8 seats · annual",
      tagline: "For a regional builder or carrier running multiple projects or policies.",
      cta: { label: "Get started" },
      featured: false,
      features: [
        "8 seats · 400 contracts / yr included",
        "Multi-project / multi-policy workspaces",
        "Workflow builder, renewal alerts & CRM connectors (Salesforce, HubSpot, Pipedrive, Dynamics) — including the embedded Salesforce Lightning panel",
        "All Team features",
        "Pay-as-you-go overage at $29 / contract",
      ],
      notIncluded: "",
    },
  ],
  platformDiff: {
    heading: "Need annual billing or full procurement-grade controls?",
    body: "The vertical-native plans are monthly self-serve, designed to start in minutes. If your buyer needs annual contracts, custom DPAs, or a full procurement review, our horizontal CLM tiers are the right fit:",
    cta: { label: "See platform pricing", anchor: "#platform-tiers" },
  },
} as const;

/* ──────────────────────────────────────────────────────────────────
 * Word Add-in Pricing
 * ────────────────────────────────────────────────────────────────── */

export const WORD_ADDIN_PRICING = {
  sectionId: "word-addin",
  eyebrow: "Word add-in · Early access",
  heading: "For solo attorneys & small teams: the Word add-in",
  subheading:
    "Run AI contract review without leaving Microsoft Word. Inline AI redlines, an executive summary, and a clause library — all inside the doc you're already in.",
  defensibilityLine:
    "Grounded in your playbook with explainable rationale — not a generic Copilot summary.",
  waitlistDialog: {
    eyebrow: "Word add-in · Early access",
    title: "Join the waitlist",
    description:
      "Drop your email and we'll notify you the moment the add-in is ready. No spam.",
    emailLabel: "Email address",
    emailPlaceholder: "jane@example.com",
    successTitle: "You're on the list",
    successBody:
      "We'll reach out as soon as the ClauseArk Word add-in is ready for early access.",
    ctaLabel: "Join the waitlist",
    ctaLoading: "Submitting…",
    privacyNote: "By submitting, you agree to our Privacy Policy.",
  },
  // `name` is the stable tier identifier sent to the backend and analytics
  // (don't rename — it would fragment historical data). `displayName` is the
  // friendly label rendered on cards and in the waitlist dialog so the two
  // surfaces match. See task #425.
  plans: [
    {
      name: "Free",
      displayName: "Solo Counsel",
      price: "$0",
      cadence: "/ mo · per seat",
      billing: "",
      tagline: "Get started with AI review at zero cost.",
      cta: { label: "Join the waitlist" },
      featured: false,
      features: [
        "AI review on standard contract types",
        "Inline AI redlines with risk badges and rationale",
        "Executive summary of key terms",
        "Clause library tab for quick reuse",
        "Matches Word's light, dark, and high-contrast themes",
      ],
      notIncluded: "Personal playbook save not included.",
    },
    {
      name: "Pro",
      displayName: "Team Pro",
      price: "$39",
      cadence: "/ mo · per seat",
      billing: "billed annually",
      tagline: "Save your own playbook positions over time.",
      cta: { label: "Join the waitlist" },
      featured: true,
      features: [
        "Everything in Free",
        "Personal playbook — save your positions over time",
        "Insert clauses from your personal playbook",
        "Priority support",
      ],
      notIncluded: "",
    },
  ],
  platformDiff: {
    heading: "What's the difference vs. the platform?",
    body: "The Word add-in is built for individual attorneys working in a single doc. The full ClauseArk platform adds team-scale capabilities your whole organization can share:",
    bullets: [
      "Shared / team playbooks across the organization",
      "Matters, public intake forms, and an intake email",
      "Workflow builder with tasks and approvals",
      "Negotiation rounds with counterparty .docx import",
      "Renewals dashboard",
      "Audit log, SSO via WorkOS, and BYOK encryption (Enterprise)",
      "E-signature via DocuSign, Adobe Sign, Dropbox Sign, or native",
    ],
    cta: { label: "See platform pricing", anchor: "#platform-tiers" },
  },
} as const;

/* ──────────────────────────────────────────────────────────────────
 * Word add-in dedicated marketing page
 * ────────────────────────────────────────────────────────────────── */

export const WORD_ADDIN_PAGE = {
  seo: {
    title: "ClauseArk Word Add-in · AI Contract Review Inside Microsoft Word",
    description:
      "Run playbook-based AI review, drop AI redlines as Word tracked changes, and insert clauses from a clause library — all without leaving Microsoft Word. Matches Word's light, dark, and high-contrast themes.",
  },
  hero: {
    eyebrow: "Word add-in · Early access",
    headline: "AI contract review, right inside Microsoft Word.",
    subhead:
      "ClauseArk's Word add-in runs playbook-based review on the doc you have open, drops AI redlines in as Word tracked changes with rationale in comments, and gives you a clause library you can insert at the cursor — without ever leaving Word.",
    primaryCta: { label: "Join the waitlist", tier: "Free" as const },
    secondaryCta: { label: "See pricing", path: "/pricing#word-addin" },
    note: "Free and Pro tiers · works in Word for the web, Mac, and Windows.",
  },
  whoItsFor: {
    headline: "Built for the reviewer who lives in Word.",
    body: "If your day is a stack of NDAs, MSAs, subcontracts, or producer agreements opened in Word, the add-in puts ClauseArk's review and clause library into the same window — not a separate tab you have to remember to switch to.",
    audiences: [
      {
        title: "Solo and small-firm attorneys",
        body: "Get playbook-based review and AI redlines on the docs in front of you, without standing up a full CLM.",
      },
      {
        title: "Construction risk and project execs",
        body: "Read counterparty AIA and ConsensusDocs paper side-aware (Owner / GC / Sub) and apply AI redlines as Word tracked changes everyone on the project already understands.",
      },
      {
        title: "Insurance brokers, MGAs, and producer ops",
        body: "Run side-aware review on producer, MGA, sub-producer, and TPA agreements (Carrier / MGA / Sub-producer / TPA / Cedent / Reinsurer) right inside the doc.",
      },
      {
        title: "Reviewers on the go",
        body: "Same add-in in Word for the web, Mac, and Windows — sign in once and your playbooks travel with you.",
      },
    ],
  },
  tabs: {
    eyebrow: "What's in the add-in",
    headline: "Four tabs, one task pane.",
    sub: "The add-in sits in Word's task pane and gives you Review, Summary, Clauses, and Material changes — each focused on a single job.",
    items: [
      {
        title: "Review",
        body: "Run playbook-based AI review on the open document, with side-aware analysis (Buyer / Seller / Carrier / Cedent and more), per-clause risk levels, and an overall risk score.",
        bullets: [
          "Pick a playbook and a side, then review the full doc clause-by-clause",
          "Risk-scored findings with rationale and a suggested redline for each issue",
          "Apply AI redlines as Word tracked changes — rationale lands as a Word comment",
          "Voice-to-Redline (beta) — push-to-talk an instruction (\"cap liability at one year of fees\") in English, Spanish, French, German, Japanese, or Brazilian Portuguese (or let auto-detect pick) and apply the AI's drafted edit as a tracked change; audio is transcribed and immediately discarded",
          "Inline diff view so you can see exactly what the redline changes",
          "Batch-apply accepted redlines, then walk the doc to confirm",
          "Save your accepted edits back to the playbook so the next review reflects them",
          "Negotiation rounds — re-run review on the counterparty's revision and see what moved",
          "Jurisdiction notes when governing law matters for a finding",
        ],
      },
      {
        title: "Summary",
        body: "Pull the parties, key economics, term, renewal, liabilities, and other key terms out of the doc — with one click to jump to the source text in Word.",
        bullets: [
          "Key-term extraction tuned to contract structure, not generic NER",
          "Each term shows the source clause, plus a Jump-to-source button that scrolls Word to it",
          "Detected governing law and jurisdiction flow into the Review tab automatically",
          "Drop-in executive summary you can paste into an email up the chain",
        ],
      },
      {
        title: "Clauses",
        body: "Browse a clause library by playbook and insert pre-approved language at your cursor — with variable prompts when a clause has placeholders.",
        bullets: [
          "Pick a playbook, browse its clauses, and insert at the cursor",
          "Clauses with {{Variable}} placeholders prompt for values before insert",
          "Variables prefill from the contract you just reviewed (parties, matter, client)",
          "Copy to clipboard as a fallback when Word isn't available",
        ],
      },
      {
        title: "Material changes",
        body: "After importing the counterparty's redline, see a plain-English summary of what they actually changed — bucketed by economic impact, legal risk, scope, and cosmetic — so you can skip re-reading the whole doc.",
        bullets: [
          "Cosmetic edits (whitespace, casing, typo fixes) are filtered out automatically",
          "Each finding is bucketed Economic, Legal risk, Scope, or Other and severity-ranked",
          "Per-finding Accept / Pushback / Acknowledge writes back to the audit log and activity feed",
          "Jump-to-clause scrolls Word to the exact text the counterparty changed",
          "Capped at 30 LLM calls per round so a noisy redline never runs up your bill",
        ],
      },
    ],
  },
  difference: {
    eyebrow: "Why not just use Copilot?",
    headline: "Different from generic AI in Word.",
    body: "Generic AI assistants in Word can summarize and rewrite. They don't know your playbook, your fallbacks, or your risk tolerance. ClauseArk's add-in does.",
    rows: [
      {
        title: "Grounded in your playbook",
        body: "Every finding and every redline maps back to a specific playbook position — not a generic style preference. You can defend it in a negotiation.",
      },
      {
        title: "Side-aware",
        body: "Review the same doc as Buyer or Seller (or Carrier / Cedent / MGA, on the insurance pack) and the suggestions change accordingly.",
      },
      {
        title: "Risk levels and an overall score",
        body: "Per-clause risk and an overall document risk so reviewers know where to spend their time first — not just a paragraph of prose.",
      },
      {
        title: "Redlines as Word tracked changes",
        body: "Edits land as native Word tracked changes with the AI's rationale in a Word comment, so the counterparty sees a normal turn of the doc.",
      },
      {
        title: "Save-to-playbook learning",
        body: "When you tweak an AI redline before accepting it, you can save the new wording back to the playbook so the next review starts from your position.",
      },
    ],
  },
  install: {
    eyebrow: "Install & getting started",
    headline: "Three steps to get the add-in into Word.",
    sub: "Today the add-in ships through early access. Once you're on the list, we'll send a manifest and a one-page install guide.",
    steps: [
      {
        n: "01",
        title: "Join the waitlist",
        body: "Drop your email below and we'll notify you when your tenant is enabled. No credit card, no sales call.",
      },
      {
        n: "02",
        title: "Sideload the manifest",
        body: "We send a Word add-in manifest you can sideload in Word for the web, Mac, or Windows — or hand to your IT admin to deploy across the org via the Microsoft 365 admin center.",
      },
      {
        n: "03",
        title: "Sign in and review",
        body: "Open the task pane, sign in with your ClauseArk account, pick a playbook, and run review on whatever doc you have open.",
      },
    ],
    fullGuide: {
      label: "Full install guide",
      path: "/resources/docs/word-addin-install",
      hint: "Sideload steps for Word for the web vs desktop, admin centralized deployment, and sign-in troubleshooting.",
    },
  },
  security: {
    eyebrow: "Security & auth",
    headline: "Same auth and tenancy as the rest of ClauseArk.",
    body: "The add-in is a thin client on top of the same backend the platform uses, so the security posture matches what you've already evaluated.",
    bullets: [
      "Sign in with your ClauseArk account — sessions are tenant-scoped and bearer-token authenticated against the same API",
      "401s automatically prompt re-authentication; tokens never leave your machine",
      "On Enterprise tenants, contracts the add-in uploads are encrypted with the same BYOK keys (AWS KMS or Azure Key Vault)",
      "Every action the add-in takes is recorded in the same per-tenant audit log",
      "Office.js custom-property storage is scoped to each open document — the add-in remembers contract and matter context per file, not globally",
    ],
  },
  compatibility: {
    eyebrow: "Theme & compatibility",
    headline: "Looks like Word, behaves like Word.",
    body: "The add-in detects Word's active theme and matches it on first paint, so the task pane never looks bolted on.",
    bullets: [
      "Matches Word's Light, Dark, and High-Contrast themes automatically",
      "Re-detects on theme changes so the task pane stays in sync",
      "Works in Word for the web, Word for Mac, and Word for Windows",
      "Falls back gracefully when Office.js isn't available — you can still paste contract text for review during dev or browser preview",
      "Per-document context (contract id, matter id) lives in Office.js custom properties so you can close and reopen a file without losing your place",
    ],
  },
  pricing: {
    eyebrow: "Pricing",
    headline: "Free for AI review. Pro saves your playbook.",
    sub: "Free includes AI review, redlines, the executive summary, and the clause library. Pro adds a personal playbook you can save your positions to.",
    primaryCta: { label: "Join the waitlist", tier: "Free" as const },
    fullPricingLink: { label: "See full pricing on the pricing page", path: "/pricing#word-addin" },
  },
  faqs: [
    {
      q: "Does the add-in replace the ClauseArk platform?",
      a: "No. The add-in is for individual attorneys working in a single doc. The platform adds shared playbooks, matters, intake forms, workflows, negotiation rounds with counterparty .docx import, the renewals dashboard, and audit/SSO/BYOK on Enterprise. Most teams use both.",
    },
    {
      q: "Where do redlines actually live?",
      a: "AI redlines are inserted as native Word tracked changes, with the AI's rationale attached as a Word comment. When you send the doc back, the counterparty sees a normal turn of the document — not a ClauseArk-specific format.",
    },
    {
      q: "Which versions of Word does it support?",
      a: "Word for the web, Word for Mac, and Word for Windows. The same add-in manifest works in all three.",
    },
    {
      q: "Do I need an admin to install it?",
      a: "You can sideload the manifest yourself for testing. For org-wide rollout, your Microsoft 365 admin can deploy the add-in centrally via the Microsoft 365 admin center.",
    },
    {
      q: "What happens if I lose connection while reviewing?",
      a: "Per-document context (contract and matter ids) is stored in Office.js custom properties, so closing and reopening the file picks up where you left off. Network calls fail with a clear error rather than silently dropping work.",
    },
  ],
} as const;

/* ──────────────────────────────────────────────────────────────────
 * Vertical pricing (Construction, Insurance)
 * ────────────────────────────────────────────────────────────────── */

export type VerticalPricingSlug = "construction" | "insurance";

/**
 * Plan slug carried into the Stripe Checkout call. Must stay in sync with
 * `MARKETING_CHECKOUT_PLANS` on the api-server side — that constant is
 * the source of truth for which slugs the public `/api/marketing/checkout`
 * endpoint accepts. Keeping the enum mirrored here means a typo in the
 * pricing card content fails at compile time on the marketing build,
 * not at click-time on the buyer's browser.
 */
export type MarketingPlanCode =
  | "construction_solo"
  | "construction_team"
  | "construction_pro"
  | "construction_payg"
  | "insurance_solo"
  | "insurance_team"
  | "insurance_pro"
  | "insurance_payg";

export interface VerticalPricingPlan {
  name: string;
  /** Slug sent to /api/marketing/checkout when the CTA is clicked. */
  plan: MarketingPlanCode;
  monthly: number;
  annualMonthly: number;
  annualTotal: number;
  seats: string;
  contracts: string;
  tagline: string;
  features: readonly string[];
  /** Button label only — no path. The page wires the click handler. */
  ctaLabel: string;
  featured?: boolean;
}

export interface VerticalPayAsYouGo {
  name: string;
  /** Slug sent to /api/marketing/checkout when the CTA is clicked. */
  plan: MarketingPlanCode;
  price: string;
  cadence: string;
  tagline: string;
  features: readonly string[];
  ctaLabel: string;
}

export interface VerticalPackHighlight {
  title: string;
  body: string;
}

export interface VerticalRoiRow {
  label: string;
  value: string;
  emphasis?: boolean;
}

export interface VerticalPricingContent {
  slug: VerticalPricingSlug;
  seo: { title: string; description: string };
  eyebrow: string;
  title: string;
  subtitle: string;
  trialCta: { label: string; plan: MarketingPlanCode };
  plans: readonly VerticalPricingPlan[];
  payAsYouGo: VerticalPayAsYouGo;
  pack: {
    eyebrow: string;
    heading: string;
    body: string;
    highlights: readonly VerticalPackHighlight[];
  };
  roi: {
    eyebrow: string;
    heading: string;
    body: string;
    rows: readonly VerticalRoiRow[];
    summary: string;
  };
  faqs: readonly { q: string; a: string }[];
  cta: {
    heading: string;
    body: string;
    primary: { label: string; plan: MarketingPlanCode };
    secondary: { label: string; path: string };
  };
}

export const VERTICAL_PRICING: Record<VerticalPricingSlug, VerticalPricingContent> = {
  construction: {
    slug: "construction",
    seo: {
      title:
        "Construction Contract Software Pricing | ClauseArk for GCs and Subs",
      description:
        "Pricing for construction legal teams. Solo, Team, and Pro plans plus pay-as-you-go — with the AIA / ConsensusDocs vertical pack, flow-down checker, and jurisdiction notes included.",
    },
    eyebrow: "Pricing · Construction",
    title: "Pricing built for construction legal teams.",
    subtitle:
      "Plans for GCs and subs running AIA, ConsensusDocs, and custom owner paper. The Construction vertical pack is included on every plan — no add-ons, no procurement loop.",
    trialCta: { label: "Start 14-day trial", plan: "construction_team" },
    plans: [
      {
        name: "Solo",
        plan: "construction_solo",
        monthly: 199,
        annualMonthly: 166,
        annualTotal: 1990,
        seats: "1 seat",
        contracts: "25 contracts / yr",
        tagline:
          "For the in-house attorney or risk lead handling AIA reviews on their own.",
        features: [
          "Construction vertical pack (AIA A201, ConsensusDocs)",
          "Playbook-based AI review with risk scoring",
          "AI redlines tied to your construction playbook",
          "Flow-down clause checker (prime → sub)",
          "Negotiation rounds with counterparty .docx",
          "Email support",
        ],
        ctaLabel: "Start 14-day trial",
      },
      {
        name: "Team",
        plan: "construction_team",
        monthly: 499,
        annualMonthly: 416,
        annualTotal: 4990,
        seats: "3 seats",
        contracts: "100 contracts / yr",
        tagline:
          "For legal + project controls running prime contracts and subs together.",
        featured: true,
        features: [
          "Everything in Solo",
          "Bulk import from SharePoint, Box, Drive, iManage, NetDocuments",
          "Renewals dashboard for insurance certs and warranties",
          "DocuSign, Adobe Sign, and Dropbox Sign",
          "Jurisdiction notes for state-specific lien and notice rules",
          "Email + chat support",
        ],
        ctaLabel: "Start 14-day trial",
      },
      {
        name: "Pro",
        plan: "construction_pro",
        monthly: 1200,
        annualMonthly: 1000,
        annualTotal: 12000,
        seats: "8 seats",
        contracts: "400 contracts / yr",
        tagline:
          "For multi-project GCs running large subcontract programs across regions.",
        features: [
          "Everything in Team",
          "Workflow builder with tasks and approvals",
          "Custom playbooks per project type or owner",
          "Salesforce and HubSpot connectors + embedded Salesforce Lightning panel",
          "Multi-language review (EN, ES, FR, DE, JA, PT-BR)",
          "Priority support with named contact",
        ],
        ctaLabel: "Start 14-day trial",
      },
    ],
    payAsYouGo: {
      name: "Pay-as-you-go",
      plan: "construction_payg",
      price: "$29",
      cadence: "/ contract reviewed",
      tagline:
        "For ad-hoc reviews — owner paper, change orders, one-off subs. No subscription, no commitment.",
      features: [
        "Same Construction vertical pack and playbooks",
        "AI review and redlines per contract",
        "1 seat, usage-based billing",
        "Upgrade to Solo, Team, or Pro any time",
      ],
      ctaLabel: "Try a contract",
    },
    pack: {
      eyebrow: "What's included",
      heading: "What's in the Construction pack",
      body: "The Construction vertical pack is on every plan — Solo through Pro and pay-as-you-go. No upsell, no separate SKU.",
      highlights: [
        {
          title: "AIA & ConsensusDocs out of the box",
          body: "AIA A201, A401, A101 and ConsensusDocs 200 / 750 contract types are pre-loaded with a construction-specific playbook.",
        },
        {
          title: "Flow-down clause checker",
          body: "Compare prime contracts to subcontracts and surface clauses that should flow down — and ones that shouldn't.",
        },
        {
          title: "Jurisdiction notes",
          body: "State-specific notes for mechanic's lien deadlines, notice requirements, and pay-when-paid enforceability.",
        },
        {
          title: "Owner paper, your way",
          body: "Custom playbooks per owner so non-AIA paper still gets reviewed against the positions your team has already negotiated.",
        },
      ],
    },
    roi: {
      eyebrow: "ROI",
      heading:
        "One AIA review at outside counsel costs more than a month of ClauseArk.",
      body: "If your construction team sends out even a handful of AIA A201 reviews a month, the math is decided before the first invoice.",
      rows: [
        {
          label: "Outside counsel: AIA A201 review",
          value: "$1,500 – $3,000 / contract",
        },
        {
          label: "ClauseArk Construction Team",
          value: "~$60 / contract",
          emphasis: true,
        },
        {
          label: "ClauseArk Construction Solo",
          value: "~$96 / contract",
        },
      ],
      summary:
        "Math: Construction Team annual ($4,990) ÷ 100 contracts ≈ $50/contract; with seats and negotiation rounds factored in, you land around $60. Pro lands closer to $30/contract at full utilization.",
    },
    faqs: [
      {
        q: "Is the vertical pack really included on every plan?",
        a: "Yes. Every Construction plan — Solo, Team, Pro, and pay-as-you-go — ships with the AIA / ConsensusDocs contract types, the construction playbook, the flow-down checker, and jurisdiction notes. There is no separate SKU to add later.",
      },
      {
        q: "What happens if I exceed my contract allowance?",
        a: "We'll let you know before you hit the cap. Overage runs at the pay-as-you-go rate of $29 per contract reviewed, billed monthly. You can also upgrade to the next tier any time.",
      },
      {
        q: "Do you handle change orders and amendments?",
        a: "Yes. Change orders, amendments, and side letters count as separate contracts and run through the same AI review and playbook flow as the underlying agreement.",
      },
      {
        q: "Can I bring counterparty paper into ClauseArk?",
        a: "Yes. Import a counterparty's .docx and ClauseArk matches their changes back to the affected clauses, round after round — designed for the negotiation cadence prime contracts and subs run on.",
      },
      {
        q: "Do I need to talk to procurement to get started?",
        a: "No. Solo, Team, Pro, and pay-as-you-go are all self-serve. Pick a plan, start the 14-day trial, and only loop in IT if you want SSO or BYOK (available on the platform Enterprise plan).",
      },
      {
        q: "What if I have more than 8 seats or 400 contracts a year?",
        a: "Talk to us. We have an Enterprise option for larger GCs that adds SSO, BYOK encryption, and a dedicated customer success contact on top of the Construction pack.",
      },
    ],
    cta: {
      heading: "Ready to get the Construction pack working on your paper?",
      body: "Start a 14-day trial — no procurement loop. Or talk to us if you need SSO, BYOK, or a Pro+ allowance.",
      primary: { label: "Start 14-day trial", plan: "construction_team" },
      secondary: { label: "Talk to sales", path: "/company/contact" },
    },
  },
  insurance: {
    slug: "insurance",
    seo: {
      title:
        "Insurance Contract Software Pricing | ClauseArk for Brokers and Carriers",
      description:
        "Pricing for insurance legal teams and brokers. Solo, Team, and Pro plans plus pay-as-you-go — with policy and endorsement review, E&O risk framing, and broker-specific playbooks included.",
    },
    eyebrow: "Pricing · Insurance",
    title: "Pricing built for insurance legal teams and brokers.",
    subtitle:
      "Plans for brokers, MGAs, and in-house carrier legal teams reviewing policies, endorsements, producer agreements, and vendor paper. The Insurance vertical pack is included on every plan.",
    trialCta: { label: "Start 14-day trial", plan: "insurance_team" },
    plans: [
      {
        name: "Solo",
        plan: "insurance_solo",
        monthly: 199,
        annualMonthly: 166,
        annualTotal: 1990,
        seats: "1 seat",
        contracts: "25 contracts / yr",
        tagline:
          "For the broker or producer attorney handling endorsement reviews on their own.",
        features: [
          "Insurance vertical pack (policies, endorsements, producer agreements)",
          "Playbook-based AI review with risk scoring",
          "AI redlines tied to your insurance playbook",
          "E&O risk framing on every clause",
          "Negotiation rounds with counterparty .docx",
          "Email support",
        ],
        ctaLabel: "Start 14-day trial",
      },
      {
        name: "Team",
        plan: "insurance_team",
        monthly: 499,
        annualMonthly: 416,
        annualTotal: 4990,
        seats: "3 seats",
        contracts: "100 contracts / yr",
        tagline:
          "For legal, compliance, and producer ops working a shared queue.",
        featured: true,
        features: [
          "Everything in Solo",
          "Bulk import from SharePoint, Box, Drive, iManage, NetDocuments",
          "Renewals dashboard for policy and producer agreement renewals",
          "DocuSign, Adobe Sign, and Dropbox Sign",
          "Carrier vs. broker form comparison",
          "Email + chat support",
        ],
        ctaLabel: "Start 14-day trial",
      },
      {
        name: "Pro",
        plan: "insurance_pro",
        monthly: 1200,
        annualMonthly: 1000,
        annualTotal: 12000,
        seats: "8 seats",
        contracts: "400 contracts / yr",
        tagline:
          "For carriers and large brokerages running multi-line, multi-state programs.",
        features: [
          "Everything in Team",
          "Workflow builder with tasks and approvals",
          "Custom playbooks per line of business or carrier",
          "Salesforce and HubSpot connectors + embedded Salesforce Lightning panel",
          "Multi-language review (EN, ES, FR, DE, JA, PT-BR)",
          "Priority support with named contact",
        ],
        ctaLabel: "Start 14-day trial",
      },
    ],
    payAsYouGo: {
      name: "Pay-as-you-go",
      plan: "insurance_payg",
      price: "$29",
      cadence: "/ contract reviewed",
      tagline:
        "For ad-hoc reviews — one-off endorsements, vendor paper, NDA traffic. No subscription, no commitment.",
      features: [
        "Same Insurance vertical pack and playbooks",
        "AI review and redlines per contract",
        "1 seat, usage-based billing",
        "Upgrade to Solo, Team, or Pro any time",
      ],
      ctaLabel: "Try a contract",
    },
    pack: {
      eyebrow: "What's included",
      heading: "What's in the Insurance pack",
      body: "The Insurance vertical pack is on every plan — Solo through Pro and pay-as-you-go. No upsell, no separate SKU.",
      highlights: [
        {
          title: "Policies and endorsements",
          body: "Pre-loaded contract types for primary policies, endorsements, manuscript wordings, and producer / broker agreements.",
        },
        {
          title: "E&O risk framing",
          body: "Every clause is scored against the E&O exposure it creates — so reviewers know which redlines actually matter.",
        },
        {
          title: "Carrier vs. broker view",
          body: "Compare the same form from the carrier's standard and the broker's mark-up side by side, with redlines tied to your playbook.",
        },
        {
          title: "Producer and vendor paper",
          body: "Producer agreements, vendor MSAs, and BAA / DPAs are first-class contract types — not generic templates.",
        },
      ],
    },
    roi: {
      eyebrow: "ROI",
      heading:
        "One outside-counsel endorsement review costs more than a month of ClauseArk.",
      body: "If your insurance team sends out even a handful of endorsement or producer-agreement reviews a month, the math is decided before the first invoice.",
      rows: [
        {
          label: "Outside counsel: endorsement / producer review",
          value: "$1,200 – $2,500 / contract",
        },
        {
          label: "ClauseArk Insurance Team",
          value: "~$60 / contract",
          emphasis: true,
        },
        {
          label: "ClauseArk Insurance Solo",
          value: "~$96 / contract",
        },
      ],
      summary:
        "Math: Insurance Team annual ($4,990) ÷ 100 contracts ≈ $50/contract; with seats and negotiation rounds factored in, you land around $60. Pro lands closer to $30/contract at full utilization.",
    },
    faqs: [
      {
        q: "Is the vertical pack really included on every plan?",
        a: "Yes. Every Insurance plan — Solo, Team, Pro, and pay-as-you-go — ships with the policy and endorsement contract types, the insurance playbook, the E&O risk framing, and producer / broker agreement templates. There is no separate SKU to add later.",
      },
      {
        q: "Do you handle manuscript wordings and one-off endorsements?",
        a: "Yes. Manuscript policies and one-off endorsements run through the same AI review and playbook flow. You can keep different playbooks per line of business or per carrier.",
      },
      {
        q: "What happens if I exceed my contract allowance?",
        a: "We'll let you know before you hit the cap. Overage runs at the pay-as-you-go rate of $29 per contract reviewed, billed monthly. You can also upgrade to the next tier any time.",
      },
      {
        q: "Can my brokerage and the carrier both use ClauseArk?",
        a: "Yes. The carrier vs. broker comparison is built into the Team plan and up — both sides can run the same contract through their own playbook and see the diff.",
      },
      {
        q: "Do I need to talk to procurement to get started?",
        a: "No. Solo, Team, Pro, and pay-as-you-go are all self-serve. Pick a plan, start the 14-day trial, and only loop in IT if you want SSO or BYOK (available on the platform Enterprise plan).",
      },
      {
        q: "What if I have more than 8 seats or 400 contracts a year?",
        a: "Talk to us. We have an Enterprise option for larger carriers and brokerages that adds SSO, BYOK encryption, and a dedicated customer success contact on top of the Insurance pack.",
      },
    ],
    cta: {
      heading: "Ready to get the Insurance pack working on your paper?",
      body: "Start a 14-day trial — no procurement loop. Or talk to us if you need SSO, BYOK, or a Pro+ allowance.",
      primary: { label: "Start 14-day trial", plan: "insurance_team" },
      secondary: { label: "Talk to sales", path: "/company/contact" },
    },
  },
};

/* ──────────────────────────────────────────────────────────────────
 * Construction Add-Ons ROI page (`/pricing/construction/add-ons`)
 *
 * Sells the three construction workflow packs on ROI rather than
 * features. Mirrors the conversion-optimized framing Procore /
 * Autodesk / Levelset use to justify add-on pricing to GCs, subs,
 * and owners. Copy intentionally talks about ROI, not list price —
 * the actual pack SKUs and Stripe pricing land later when the pack
 * feature work ships (#591 and #593–#604).
 * ────────────────────────────────────────────────────────────────── */

export interface ConstructionAddonRoiPack {
  /** Stable id used in DOM testids and section anchors. */
  id: "subcontractor-compliance" | "change-order-claims" | "lien-pay-app";
  title: string;
  tagline: string;
  withoutClauseArk: readonly string[];
  withClauseArk: readonly string[];
  typicalSavings: readonly string[];
  bottomLine: string;
}

export interface ConstructionAddonsRoiContent {
  seo: { title: string; description: string };
  hero: {
    eyebrow: string;
    headline: string;
    subhead: string;
  };
  packs: readonly ConstructionAddonRoiPack[];
  bundle: {
    eyebrow: string;
    heading: string;
    body: string;
    impacts: readonly string[];
    primaryCta: { label: string; path: string };
    secondaryCta: { label: string; path: string };
  };
}

export const CONSTRUCTION_ADDONS_ROI: ConstructionAddonsRoiContent = {
  seo: {
    title:
      "Construction Workflow Pack ROI | Subcontractor, Change Order, and Lien Add-ons | ClauseArk",
    description:
      "ROI breakdown for the three ClauseArk construction workflow packs — Subcontractor Contract + Compliance, Change Order + Claims, and Lien Waiver + Pay Application. Typical per-contract and per-project savings for GCs, subs, and owners.",
  },
  hero: {
    eyebrow: "Construction · Workflow pack ROI",
    headline: "Each workflow pack pays for itself in the first week.",
    subhead:
      "Construction legal work is slow, expensive, and risky — subcontractor onboarding takes weeks, change orders pile up unsigned, and a single missed lien waiver can hold a draw for months. Each ClauseArk workflow pack is built to take one of those problem areas off your team's plate. Here's the math.",
  },
  packs: [
    {
      id: "subcontractor-compliance",
      title: "Subcontractor Contract + Compliance Pack",
      tagline:
        "Onboard subs in days, not weeks — with COIs, W-9s, and prequal in one place.",
      withoutClauseArk: [
        "Outside counsel reviews each subcontract at $1,500–$3,000 per agreement.",
        "Project teams chase COIs, W-9s, safety plans, and prequal forms over email for 2–4 weeks per sub.",
        "Missed insurance endorsements and non-compliant indemnity language surface mid-project — when changes are most expensive.",
        "Prime flow-down obligations get copied by hand, so subs sign paper that doesn't actually mirror the prime contract.",
      ],
      withClauseArk: [
        "AI review against your construction playbook in minutes — flow-downs auto-checked against the prime.",
        "COIs, W-9s, prequal questionnaires, and safety attestations gathered through a single sub portal.",
        "Insurance endorsement and indemnity clauses flagged before signature, with a suggested redline for each.",
        "Compliance dashboard shows which subs are clear to mobilize and which are still missing a document.",
      ],
      typicalSavings: [
        "$450 – $1,200 per subcontractor in legal review fees",
        "$15,000 – $60,000 per project in avoided rework and dispute exposure",
        "80% reduction in compliance risk from missing or non-conforming insurance certificates",
      ],
      bottomLine:
        "Most GCs recover the cost of this add-on after onboarding 2–3 subcontractors.",
    },
    {
      id: "change-order-claims",
      title: "Change Order + Claims Pack",
      tagline:
        "Cut change-order cycles in half — and stop letting unsigned COs become claims.",
      withoutClauseArk: [
        "Change orders sit unsigned for weeks while PMs, subs, and owners trade redlines over email.",
        "Notice deadlines (often 7–21 days under AIA / ConsensusDocs) get missed because nobody owns the calendar.",
        "Claim packages are assembled from scratch every time — daily reports, RFIs, schedule fragnets, and cost backup.",
        "Outside counsel reviews disputed COs and claim demands at $400 – $800/hr.",
      ],
      withClauseArk: [
        "AI review of every change order against the underlying contract's pricing, scheduling, and notice rules.",
        "Notice deadlines tracked automatically the moment the change event is logged.",
        "Claim packages assembled from linked daily reports, RFIs, schedule data, and cost records — not email folders.",
        "Per-clause risk scoring shows which COs and claim positions are defensible and which are not.",
      ],
      typicalSavings: [
        "$300 – $1,000 per change order in cycle time and review fees",
        "$10,000 – $100,000 per claim in avoided dispute escalation and outside-counsel spend",
        "50 – 70% faster change-order approval cycle, end-to-end",
      ],
      bottomLine:
        "One prevented dispute pays for this add-on for the entire year.",
    },
    {
      id: "lien-pay-app",
      title: "Lien Waiver + Pay Application Pack",
      tagline:
        "Get pay apps out the door without lien-waiver chaos.",
      withoutClauseArk: [
        "Conditional and unconditional lien waivers are filled in by hand for every sub on every draw.",
        "Pay applications get rejected by the owner because backup or waiver forms don't match jurisdiction-specific statutory language.",
        "Lower-tier waivers come back missing or signed against the wrong amount, leaving the GC exposed.",
        "Late or invalid waivers hold draws for weeks and force the prime to carry payroll out of pocket.",
      ],
      withClauseArk: [
        "Statutory waiver forms pre-loaded for every U.S. state, auto-populated from each pay application's amounts.",
        "Conditional and unconditional waiver flow tracked across the prime, subs, and lower-tier suppliers.",
        "AIA G702 / G703 (or owner-specific) pay applications generated from approved cost data, with backup attached.",
        "A single dashboard shows which waivers are outstanding before submitting the draw.",
      ],
      typicalSavings: [
        "$150 – $500 per pay application in admin time and rework",
        "$5,000 – $50,000 per year in avoided lien filings, draw delays, and waiver-mismatch write-offs",
        "Faster draws — fewer working capital gaps between approved billings and funded payments",
      ],
      bottomLine:
        "If your team processes more than 5 pay apps per month, this add-on pays for itself.",
    },
  ],
  bundle: {
    eyebrow: "Bundle ROI · All three packs",
    heading: "The Construction Legal OS — all three packs working together.",
    body: "Run subcontractor onboarding, change orders, and pay-app + lien workflows on the same source of truth. The packs share contract, compliance, and cost data, so subs you onboard flow into the right COs, and the COs you approve flow into the right pay apps. The combined impact is more than the sum of the parts.",
    impacts: [
      "40 – 100 hours saved per project across legal, project controls, and accounting",
      "$25,000 – $250,000 per year in combined dispute, rework, and outside-counsel savings — depending on project mix",
      "5× – 15× ROI on the bundle within 90 days of go-live",
      "One audit trail across subs, change orders, waivers, and pay apps — defensible if a dispute ever does land",
    ],
    primaryCta: { label: "See Construction plans", path: "/pricing/construction" },
    secondaryCta: { label: "Talk to sales", path: "/company/contact" },
  },
};

/* ──────────────────────────────────────────────────────────────────
 * Resources
 * ────────────────────────────────────────────────────────────────── */

export const BLOG_POSTS: ReadonlyArray<{
  slug: string;
  title: string;
  excerpt: string;
  author: string;
  date: string;
  readMin: number;
  tag: string;
}> = [];

/* ──────────────────────────────────────────────────────────────────
 * Word add-in install guide (linked from /word-addin and /resources/docs)
 * ────────────────────────────────────────────────────────────────── */

export const WORD_ADDIN_INSTALL_GUIDE = {
  slug: "word-addin-install",
  seo: {
    title: "Install the ClauseArk Word add-in · Self-service guide",
    description:
      "Step-by-step guide to install the ClauseArk Word add-in: prerequisites, sideload steps for Word for the web and desktop, centralized deployment via the Microsoft 365 admin center, and sign-in / theme troubleshooting.",
  },
  hero: {
    eyebrow: "Documentation · Word add-in",
    title: "Install the ClauseArk Word add-in",
    subtitle:
      "A self-service install guide for evaluators and IT admins. Sideload it on a single account in a few minutes, or deploy it tenant-wide from the Microsoft 365 admin center.",
    estimatedTime: "Sideload: ~5 minutes · Centralized deployment: ~15 minutes",
  },
  manifest: {
    label: "Manifest URL",
    url: "https://addin.clauseark.com/manifest.xml",
    note: "Hosted by ClauseArk and signed for Office. Use this same URL for sideloading and for centralized deployment.",
  },
  prerequisites: {
    title: "Prerequisites",
    body: "Before you start, confirm your Microsoft 365 setup supports Office add-ins and that you have a ClauseArk account.",
    items: [
      "A Microsoft 365 work or school account with Word for the web, Word for Mac (16.77+), or Word for Windows (Microsoft 365 build 2206+).",
      "Word add-ins enabled for your tenant — most tenants allow this by default; check the Microsoft 365 admin center under Settings → Integrated apps if you're not sure.",
      "An invited ClauseArk account on the tenant you'll be reviewing for. Ask your ClauseArk admin to invite you, or join the waitlist from the /word-addin page.",
      "On Windows, Edge WebView2 Runtime installed (it ships with Windows 11 and recent Microsoft 365 updates).",
      "Outbound HTTPS access to addin.clauseark.com and api.clauseark.com from the machine running Word.",
    ],
  },
  sideloadWeb: {
    title: "Sideload in Word for the web",
    intro:
      "Fastest way to try the add-in. Works on any browser-supported tenant, no admin rights needed.",
    steps: [
      "Open the document you want to review at https://word.cloud.microsoft (or from OneDrive / SharePoint).",
      "On the ribbon, click Home → Add-ins → More add-ins.",
      "In the Office Add-ins dialog, switch to the Upload My Add-in tab.",
      "Paste the manifest URL above (or upload a manifest.xml file your account manager sent you) and click Upload.",
      "ClauseArk will appear in the ribbon under the Home tab. Click it to open the task pane.",
      "Sign in with your ClauseArk account when prompted, then pick a playbook and run review.",
    ],
    tip: "If you don't see the Upload My Add-in tab, your tenant's admin has disabled sideloading. Use the centralized deployment path below instead.",
  },
  sideloadDesktop: {
    title: "Sideload in Word for Mac and Windows",
    intro:
      "Sideloading on the desktop apps requires a one-time setup using a shared folder catalog (Windows) or the manifests folder (Mac).",
    macSteps: [
      "Save the manifest.xml file from your account manager to ~/Library/Containers/com.microsoft.Word/Data/Documents/wef. Create the wef folder if it doesn't exist.",
      "Quit and reopen Word. Open any document.",
      "Click Insert → Add-ins → My Add-ins, then choose the Developer Add-ins tab.",
      "Select ClauseArk and click Add. The task pane opens on the right.",
    ],
    windowsSteps: [
      "Create a shared folder on your machine or a network share — for example, C:\\AddinCatalog. Right-click → Properties → Sharing and share it with at least Read access for your account.",
      "Copy the manifest.xml file into that folder.",
      "In Word, go to File → Options → Trust Center → Trust Center Settings → Trusted Add-in Catalogs.",
      "Paste the network path to the folder (e.g., \\\\YOUR-PC\\AddinCatalog), check Show in menu, then click Add catalog and OK.",
      "Restart Word, open any document, and choose Insert → My Add-ins → Shared Folder. Pick ClauseArk and click Add.",
    ],
    tip: "Sideloading is ideal for trial accounts. For production, prefer centralized deployment so updates roll out automatically.",
  },
  centralizedDeployment: {
    title: "Deploy across your organization",
    intro:
      "Recommended for production. Microsoft 365 admins can push the add-in to all users (or specific groups) and updates ship automatically — users see ClauseArk in Word's ribbon the next time they open a document.",
    steps: [
      "Sign in to admin.microsoft.com as a Global or Application Admin.",
      "Go to Settings → Integrated apps → Get apps.",
      "Click Upload custom apps, choose Office Add-in, then provide the manifest URL above (or upload the manifest.xml).",
      "On the Users step, choose Everyone, Specific users/groups, or Just me. Most teams start with a pilot group and broaden from there.",
      "Review the requested permissions (read/write the active document, network access to api.clauseark.com) and click Finish deployment.",
      "Allow up to 6 hours for the add-in to appear in Word for all targeted users. New users typically see it on next launch.",
    ],
    callouts: [
      "Updates to the add-in are automatic — you do not need to redeploy when ClauseArk ships a new version.",
      "To remove the add-in, return to Integrated apps, select ClauseArk, and click Remove. Users lose access at next launch.",
      "Group Policy and Cloud Policy customers: the add-in honors the standard \"Block access to the Office Store\" and \"Allow only trusted publishers\" policies — ClauseArk is published by ClauseArk, Inc.",
    ],
  },
  signInTroubleshooting: {
    title: "Sign-in and authentication troubleshooting",
    body: "The add-in uses the same bearer-token auth as the rest of ClauseArk. If sign-in misbehaves, it's almost always one of these:",
    items: [
      {
        symptom: "Sign-in popup opens, then closes immediately without signing me in.",
        fix: "Allow third-party cookies for clauseark.com in the browser running Word for the web. In Edge: Settings → Cookies and site permissions → Cookies → Allow → add [*.]clauseark.com.",
      },
      {
        symptom: "I get \"401 Unauthorized\" after signing in successfully.",
        fix: "Your ClauseArk account isn't on the tenant you're reviewing for. Ask your tenant admin to invite you (Settings → Members in ClauseArk), then sign out and back in from the task pane menu.",
      },
      {
        symptom: "The task pane is stuck on a loading spinner.",
        fix: "Close and reopen the task pane (Insert → My Add-ins → ClauseArk). If it persists, check that api.clauseark.com is reachable from the machine — corporate proxies sometimes block it.",
      },
      {
        symptom: "I'm signed in but the playbook list is empty.",
        fix: "Your account is on a tenant with no playbooks yet. Create one in the ClauseArk web app (Playbooks → New) and refresh the task pane.",
      },
      {
        symptom: "I see \"Office.js is not available\" in the task pane.",
        fix: "You opened the add-in URL in a regular browser tab instead of inside Word. The add-in only works from Word's task pane — open it from the ribbon (Home → ClauseArk).",
      },
    ],
  },
  themeAndCompatibility: {
    title: "Theme and compatibility notes",
    body: "The add-in is built to feel native to Word — it should never look bolted on, regardless of theme or platform.",
    items: [
      "Matches Word's Light, Dark, and High-Contrast themes automatically on first paint, and re-detects when you change theme without needing to reopen the task pane.",
      "Tested on Word for the web (Edge, Chrome, Safari, Firefox), Word for Mac 16.77+, and Word for Windows (Microsoft 365 build 2206+).",
      "Word for iPad and Word for Android are not officially supported yet — the manifest installs, but the task pane is too narrow for a useful experience.",
      "Per-document context (contract id, matter id, last review run) is stored in Office.js custom properties, so closing and reopening a file picks up where you left off.",
      "The task pane gracefully falls back to a paste-text mode if Office.js fails to initialize — useful when previewing during dev or in browsers that block Office.js for sandboxed iframes.",
    ],
  },
  nextSteps: {
    title: "Next steps",
    items: [
      {
        label: "Read the Word add-in overview",
        path: "/word-addin",
        description: "Tabs, redlines, side-aware review, and how the add-in differs from generic AI in Word.",
      },
      {
        label: "See pricing",
        path: "/pricing#word-addin",
        description: "Free and Pro tiers for the Word add-in, plus how it relates to the platform.",
      },
      {
        label: "Talk to us",
        path: "/company/contact",
        description: "Hit a snag the troubleshooting list didn't cover? Email us or book a quick call.",
      },
    ],
  },
} as const;

export const SALESFORCE_ADMIN_GUIDE = {
  slug: "salesforce-admin-guide",
  seo: {
    title: "Install the ClauseArk Salesforce embedded panel · Admin guide",
    description:
      "Step-by-step guide for Salesforce admins: deploy the ClauseArk Lightning Web Component, configure the per-org embed shared-secret, assign the permission set, and drop the panel onto Opportunity, Account, and Quote pages in Lightning App Builder.",
  },
  hero: {
    eyebrow: "Documentation · Salesforce embedded UI",
    title: "Install the ClauseArk Salesforce panel",
    subtitle:
      "Self-service install guide for Salesforce admins. Deploy the unmanaged source, configure one custom-metadata record, assign the permission set, and add the ClauseArk panel to Opportunity, Account, and Quote pages in a few minutes.",
    estimatedTime: "Deploy + configure: ~15 minutes · Lightning App Builder: ~5 minutes",
  },
  sourcePackage: {
    label: "Source package",
    location: "salesforce-package/ in the ClauseArk repository",
    note: "Ships as a Salesforce DX source tree. Deploy with `sf project deploy start --source-dir force-app` or repackage as an Unlocked Package for AppExchange-style distribution.",
  },
  prerequisites: {
    title: "Prerequisites",
    body: "Confirm your Salesforce org and ClauseArk workspace are ready before you start.",
    items: [
      "A Salesforce org running Lightning Experience. Salesforce Classic is not supported.",
      "Salesforce CLI (`sf`) installed locally, or an admin who can deploy unmanaged metadata via the Setup → Deploy / Retrieve Metadata flow.",
      "A ClauseArk admin account on a Growth-or-higher plan (Salesforce / CRM connectors are gated to Growth+).",
      "The Salesforce CRM connector already connected in ClauseArk (Settings → Integrations → Salesforce → Connect). The embedded panel sits on top of the existing connector — it does not re-authenticate Salesforce.",
      "Outbound HTTPS access from your Salesforce org to api.clauseark.com (or your regional API host).",
    ],
  },
  rotateSecret: {
    title: "Rotate the embed shared-secret",
    intro:
      "The embedded panel uses a per-tenant shared secret to sign Apex callouts. Rotating issues a new value and immediately invalidates any previous one — admins can rotate anytime without redeploying the LWC.",
    steps: [
      "Sign in to ClauseArk as a workspace admin.",
      "Open Settings → Integrations → Salesforce. The Embedded panel card appears once the connector is active.",
      "Click \"Rotate embed secret\". Copy the value shown — this is the only time it is displayed in plain text.",
      "Paste it into the EmbeddedSecret field of your ClauseArk_Setting__mdt 'Default' record in Salesforce (Setup → Custom Metadata Types → ClauseArk Setting → Manage Records → Default).",
    ],
    tip: "Treat the embed secret as a credential. Anyone with the value can mint Salesforce-scoped callouts to your ClauseArk workspace, so store it in your password manager and rotate immediately if a copy leaks.",
  },
  deploySource: {
    title: "Deploy the source package",
    intro:
      "Deploy the LWC, Apex controller, custom metadata type, permission set, and remote site setting from the salesforce-package/ directory in your ClauseArk source tree.",
    steps: [
      "Clone (or pull) the ClauseArk repository so you have the salesforce-package/ directory on disk.",
      "Authorize the target Salesforce org: `sf org login web --alias clauseark-prod`.",
      "Deploy the source: `cd salesforce-package && sf project deploy start --source-dir force-app --target-org clauseark-prod`.",
      "Confirm in Salesforce Setup → Apex Classes that ClauseArkApiController is present, and Setup → Custom Metadata Types lists ClauseArk Setting.",
    ],
    tip: "If you prefer the UI flow, zip the force-app directory and upload via Setup → Deploy / Retrieve Metadata. The result is identical.",
  },
  configureMetadata: {
    title: "Configure the per-org settings record",
    intro:
      "The ClauseArk_Setting__mdt 'Default' record carries every value the LWC needs at runtime. Update the placeholder values that ship in the source package.",
    steps: [
      "Setup → Custom Metadata Types → ClauseArk Setting → Manage Records → Default → Edit.",
      "Set Tenant Id to your ClauseArk workspace id (Settings → Workspace → Workspace ID).",
      "Set API Base URL to https://api.clauseark.com (or your regional / sandbox URL).",
      "Set Web App Base URL to https://app.clauseark.com (or your sandbox web-app host). Leave blank to derive from API Base URL.",
      "Paste the Embedded Secret you rotated in ClauseArk into the EmbeddedSecret field. Save.",
    ],
    tip: "Custom metadata records are deployable, so you can promote the same configuration through sandbox → staging → production without re-typing values — but never check the Embedded Secret into a sandbox metadata file.",
  },
  permissionSet: {
    title: "Assign the permission set",
    intro:
      "Only users with the ClauseArk Embedded permission set can see the panel. The permission set grants Read access to ClauseArk_Setting__mdt and the Apex controller.",
    steps: [
      "Setup → Permission Sets → ClauseArk Embedded → Manage Assignments → Add Assignments.",
      "Select every user who should see the panel — typically Sales, Sales Ops, and Legal users who work opportunities.",
      "Click Assign and confirm.",
    ],
    callouts: [
      "The ClauseArk side enforces RBAC independently — Salesforce users are mapped to ClauseArk users by email, and ClauseArk roles still gate write operations like \"Start a contract\".",
      "Salesforce sharing rules continue to apply — users only see opportunities they already have access to in Salesforce.",
      "Removing the permission set instantly hides the panel for that user, no cache flush required.",
    ],
  },
  addToLayouts: {
    title: "Add the panel to record pages",
    intro:
      "Use Lightning App Builder to drop the ClauseArk Contracts component onto your Opportunity, Account, and Quote record pages.",
    steps: [
      "Open an Opportunity record → gear icon → Edit Page.",
      "Drag the ClauseArk Contracts component from the Custom palette onto the right rail (or wherever the layout has room).",
      "Save the page. Activate it for the relevant Org Default or App-specific assignment.",
      "Repeat for Account and Quote record pages if you want the panel there too.",
    ],
    tip: "The component is set to render on Opportunity, Account, and Quote out of the box. If you want to add it elsewhere (e.g. Custom Object), add the object's API name to the targets list in clauseArkContractsPanel.js-meta.xml and redeploy.",
  },
  whatItDoes: {
    title: "What the panel does",
    body: "Once installed, the panel shows up on every record page where you've added it.",
    items: [
      "Lists ClauseArk contracts linked to the opportunity, with status, counterparty, and a deep-link to open the contract in ClauseArk.",
      "Includes a \"Start a contract\" button that pre-fills the new contract with the opportunity's name, account, value, contact, and close date — and links the resulting contract back to the opportunity.",
      "Renders inside an iframe pointed at the ClauseArk web app, so any UI improvements ship to the panel automatically — no Salesforce redeploy required.",
      "Honors ClauseArk feature gating — the panel only renders when the workspace is on Growth or higher, and read/write actions respect ClauseArk role assignments.",
    ],
  },
  troubleshooting: {
    title: "Troubleshooting",
    body: "If something looks off, the most common causes are:",
    items: [
      {
        symptom: "The panel shows \"Couldn't open ClauseArk\" / 401.",
        fix: "Re-paste the EmbeddedSecret in the Custom Metadata record — make sure no whitespace was copied. If the secret was rotated again on the ClauseArk side, the previous value is invalid.",
      },
      {
        symptom: "The panel shows \"Salesforce user is not provisioned in this ClauseArk workspace\".",
        fix: "The Salesforce running user's email must match a ClauseArk user on the same workspace. Invite the user from ClauseArk Settings → Members, or update the Salesforce user's email to match.",
      },
      {
        symptom: "Apex callout fails with \"Unauthorized endpoint\".",
        fix: "Add (or re-enable) the ClauseArk_API Remote Site Setting in Salesforce Setup → Remote Site Settings, with URL https://api.clauseark.com.",
      },
      {
        symptom: "I deployed but the LWC isn't in the Lightning App Builder palette.",
        fix: "Confirm the deployment included the lwc/ directory and that the user editing the page has the ClauseArk Embedded permission set assigned. The component only appears in the palette for users who can use the Apex controller.",
      },
      {
        symptom: "\"Start a contract\" succeeds but the contract is missing the close date.",
        fix: "ClauseArk stores the close date as audit-log metadata, not as a top-level contract field. The contract is still linked to the opportunity, and the close date is visible in the opportunity's audit-log entry.",
      },
    ],
  },
  outOfScope: {
    title: "What's not in this release",
    body: "We deliberately scoped the first release to the highest-impact surface. The following are tracked but not shipped here:",
    items: [
      "An AppExchange listing — for now, install via the source package (or repackage internally as an Unlocked Package).",
      "Embedded support for HubSpot, Pipedrive, and Microsoft Dynamics — this release is Salesforce-only.",
      "In-iframe redline editing — opening a contract launches the full ClauseArk web app in a new tab, where the redline editor lives.",
      "New Salesforce field syncs beyond the existing connector's contract status / last-updated mirror.",
    ],
  },
  nextSteps: {
    title: "Next steps",
    items: [
      {
        label: "Configure the Salesforce CRM connector",
        path: "/platform/integrations",
        description: "The embedded panel sits on top of the standard CRM connector — connect it first if you haven't already.",
      },
      {
        label: "See pricing",
        path: "/pricing#platform-tiers",
        description: "The embedded Salesforce panel is available on the Growth tier and above.",
      },
      {
        label: "Talk to us",
        path: "/company/contact",
        description: "Need help with the install or want a guided walkthrough? We're happy to help.",
      },
    ],
  },
} as const;

export const DOCS_SECTIONS: ReadonlyArray<{
  title: string;
  items: ReadonlyArray<{ label: string; body: string; path?: string }>;
}> = [
  {
    title: "Getting started",
    items: [
      {
        label: "Set up your tenant",
        body: "Invite teammates, choose your playbook starting point (General, Construction, or Insurance), and connect a document source.",
      },
      {
        label: "Upload your first contract",
        body: "Drop in a PDF or DOCX (scanned PDFs are OCR'd automatically), choose a playbook, and run review.",
      },
      {
        label: "Open an intake channel",
        body: "Stand up a public intake form or an intake email address so the rest of the business can submit requests.",
      },
    ],
  },
  {
    title: "Review & redlines",
    items: [
      {
        label: "Reading the risk score",
        body: "How per-clause risk levels and the overall contract risk score are computed against your playbook.",
      },
      {
        label: "Working with AI redlines",
        body: "Accept, edit, or reject suggested redlines and see which playbook position each one implements.",
      },
      {
        label: "Negotiation rounds",
        body: "Import the counterparty's revised .docx and see how their changes match back to your clauses.",
      },
    ],
  },
  {
    title: "Lifecycle",
    items: [
      {
        label: "Matters",
        body: "Group contracts, attachments, and amendments under a single matter record.",
      },
      {
        label: "Workflow builder",
        body: "Build approval flows with the tasks each step needs.",
      },
      {
        label: "Renewals dashboard",
        body: "Track upcoming renewals and termination windows in one place.",
      },
    ],
  },
  {
    title: "Trust & access",
    items: [
      {
        label: "SSO via WorkOS",
        body: "Connect SAML or OIDC SSO for Okta, Microsoft Entra ID, Google Workspace, and more.",
      },
      {
        label: "BYOK encryption",
        body: "Bring your own keys via AWS KMS or Azure Key Vault.",
      },
      {
        label: "Audit log",
        body: "What's recorded in the per-tenant audit log and how to read it.",
      },
    ],
  },
  {
    title: "Word add-in",
    items: [
      {
        label: "Install the Word add-in →",
        body: "Self-service install guide: prerequisites, sideload steps for Word for the web vs desktop, centralized deployment via the Microsoft 365 admin center, and sign-in / theme troubleshooting.",
        path: "/resources/docs/word-addin-install",
      },
    ],
  },
  {
    title: "Salesforce embedded UI",
    items: [
      {
        label: "Install the Salesforce panel →",
        body: "Admin guide for the ClauseArk Lightning Web Component: deploy the unmanaged source, rotate the embed shared-secret, assign the permission set, and add the panel to Opportunity, Account, and Quote pages.",
        path: "/resources/docs/salesforce-admin-guide",
      },
    ],
  },
  {
    title: "Developers",
    items: [
      {
        label: "Outbound webhooks reference →",
        body: "Wire ClauseArk obligations and key dates into Jira, NetSuite, ServiceNow, or any HTTPS endpoint. Envelope shape, HMAC-SHA256 signing, retry schedule, and copy-pasteable verifier snippets in Node, Python, and Ruby.",
        path: "/resources/docs/webhooks",
      },
    ],
  },
];

/* ──────────────────────────────────────────────────────────────────
 * Public outbound-webhooks reference (Task #741)
 *
 * The internal contract lives at `docs/outbound-webhooks.md` and the
 * typed Zod schemas live in `artifacts/api-server/src/lib/outboundEvents.ts`.
 * This module is the customer-facing copy of the same contract,
 * structured so that a regression test can pin the event list and the
 * verifier snippets stay in sync. Any change to the wire format must
 * also land here.
 * ────────────────────────────────────────────────────────────────── */

export type WebhookEventName =
  | "obligation.created"
  | "obligation.updated"
  | "obligation.completed"
  | "obligation.due_soon"
  | "key_date.created"
  | "key_date.changed";

export interface WebhookEventDoc {
  name: WebhookEventName;
  fires: string;
  examplePayload: Record<string, unknown>;
}

const SAMPLE_CONTRACT = {
  id: "8f1e2c34-9b6a-4d7e-bf12-2c3a4d5e6f78",
  title: "Acme MSA",
  contractType: "MSA",
  status: "executed",
  counterpartyName: "Acme Corp",
} as const;

const SAMPLE_MATTER = {
  id: "12c0fa6e-77e2-4b88-a7d1-9b35c4f6a112",
  name: "Acme onboarding",
} as const;

const SAMPLE_OBLIGATION = {
  id: "3a9d1e5b-22a1-4c8e-9f3a-7e1c4b2a8d09",
  kind: "audit",
  title: "Quarterly SOC 2 attestation",
  description: "Counterparty must deliver SOC 2 Type II report each quarter.",
  dueDate: "2026-05-15",
  recurrence: "quarterly",
  status: "open",
  ownerEmail: "ops@acme.com",
  ownerName: "Sam Ops",
  source: "ai_extracted",
  createdAt: "2026-04-15T17:00:00.000Z",
  updatedAt: "2026-04-29T09:11:42.000Z",
} as const;

const SAMPLE_KEY_DATE = {
  id: "7b21c4f8-1d52-43e1-b7c8-4f8a2e7c1a55",
  kind: "auto_renew_date",
  keyDate: "2026-12-01",
  notes: "Auto-renews for a 12-month term unless notice is given by Nov 1.",
  createdAt: "2026-04-15T17:00:00.000Z",
  updatedAt: "2026-04-15T17:00:00.000Z",
} as const;

export const WEBHOOK_DOCS = {
  seo: {
    title: "Outbound webhooks · ClauseArk developer docs",
    description:
      "ClauseArk outbound webhooks reference: envelope shape, HMAC-SHA256 signing, retry schedule, and verifier snippets in Node, Python, and Ruby.",
  },
  hero: {
    eyebrow: "Developers · Outbound webhooks",
    title: "Outbound webhooks",
    subtitle:
      "ClauseArk pushes obligations and key dates to your tools the moment they change. This is the public reference for the wire format, signing, and retry policy — the same contract our in-product Settings → Outbound webhooks page connects to.",
  },
  envelope: {
    title: "The envelope",
    body:
      "Every event is a single, stable JSON envelope signed with HMAC-SHA256, retried for up to 7 days, with a per-endpoint dead-letter view in the UI for debugging.",
    code: `POST https://your-receiver.example.com/clauseark
Content-Type: application/json
User-Agent: ClauseArk-Webhook/1.0
X-ClauseArk-Event: obligation.due_soon
X-ClauseArk-Delivery: 4d3a8a6e-88f7-4a4b-9f4e-3f9b1c11d3a0
X-ClauseArk-Timestamp: 1763404800
X-ClauseArk-Signature: t=1763404800,v1=4f9c…cf01

{
  "id":   "4d3a8a6e-88f7-4a4b-9f4e-3f9b1c11d3a0",
  "type": "obligation.due_soon",
  "v":    1,
  "createdAt": "2026-04-29T17:00:00.000Z",
  "data": { ... }
}`,
    notes: [
      "X-ClauseArk-Delivery equals envelope.id and is the idempotency key — every retry sends the same value.",
      "All current events ship with v: 1. Future shape changes will bump v rather than minting a new event name; receivers should branch on v only when they need a field added in v: 2.",
    ],
  },
  events: [
    {
      name: "obligation.created",
      fires: "A new obligation row exists (admin add or AI extraction).",
      examplePayload: {
        obligation: SAMPLE_OBLIGATION,
        contract: SAMPLE_CONTRACT,
        matter: SAMPLE_MATTER,
        reason: "ai_extracted",
      },
    },
    {
      name: "obligation.updated",
      fires: "An admin PATCHes an obligation (title, owner, due date, status, …).",
      examplePayload: {
        obligation: { ...SAMPLE_OBLIGATION, status: "in_progress" },
        contract: SAMPLE_CONTRACT,
        matter: SAMPLE_MATTER,
        reason: "patch",
      },
    },
    {
      name: "obligation.completed",
      fires:
        "An obligation is marked done (mark_done) or a recurring obligation rolls forward (cycle_completed).",
      examplePayload: {
        obligation: { ...SAMPLE_OBLIGATION, status: "done" },
        contract: SAMPLE_CONTRACT,
        matter: SAMPLE_MATTER,
        reason: "mark_done",
      },
    },
    {
      name: "obligation.due_soon",
      fires:
        "An obligation crosses an alert threshold (typically 7 / 3 / 1 days before due).",
      examplePayload: {
        obligation: SAMPLE_OBLIGATION,
        contract: SAMPLE_CONTRACT,
        matter: SAMPLE_MATTER,
        daysUntilDue: 3,
      },
    },
    {
      name: "key_date.created",
      fires: "A key date (effective, term-end, auto-renew, notice deadline) is added.",
      examplePayload: {
        keyDate: SAMPLE_KEY_DATE,
        contract: SAMPLE_CONTRACT,
        matter: SAMPLE_MATTER,
        reason: "ai_extracted",
      },
    },
    {
      name: "key_date.changed",
      fires: "An existing key date moves.",
      examplePayload: {
        keyDate: { ...SAMPLE_KEY_DATE, keyDate: "2027-01-01" },
        contract: SAMPLE_CONTRACT,
        matter: SAMPLE_MATTER,
        reason: "changed",
      },
    },
  ] as ReadonlyArray<WebhookEventDoc>,
  filters: {
    title: "Filters",
    body:
      "A subscription's filters object narrows which events fire to which endpoint. All keys are optional; within a key the values are OR'd, across keys they're AND'd. A subscription with filters: {} matches everything.",
    code: `{
  "matterIds":         ["…", "…"],
  "contractIds":       ["…"],
  "counterpartyNames": ["Acme Corp"],
  "contractTypes":     ["MSA", "DPA"]
}`,
  },
  signing: {
    title: "Signing",
    body:
      "The signature header is Stripe-shaped on purpose so receiver snippets you've copy-pasted from other vendors keep working. To verify, recompute the HMAC over the literal `${t}.${rawBody}` byte string using the secret you stored at subscription time and compare with constant-time equality. Reject signatures whose t differs from now() by more than 5 minutes — that prevents replay even if your endpoint is also reachable on the open internet.",
    headerFormat:
      "X-ClauseArk-Signature: t=<unix-seconds>,v1=<hex hmac-sha256(secret, \"${t}.${rawBody}\")>",
    rotationNote:
      "POST /api/webhooks/outbound/:id/rotate-secret returns a new secret in the response body. The new secret takes effect immediately for the next delivery; in-flight retries continue under the old secret until they complete or dead-letter, so plan a brief overlap window in your receiver during a rotation.",
  },
  retry: {
    title: "Retry policy",
    body:
      "Any non-2xx HTTP response (or a transport error) re-arms the row for a later attempt with ±20% jitter applied to each backoff. After the 12th failed attempt — or after 7 days regardless of how many attempts have happened — the delivery is flagged dead and never retried. Dead-lettered deliveries appear in Settings → Outbound webhooks → (endpoint) → Deliveries with their last response code and body so you can debug without leaving the product.",
    schedule: [
      { attempt: "1", wait: "1 minute" },
      { attempt: "2", wait: "5 minutes" },
      { attempt: "3", wait: "30 minutes" },
      { attempt: "4", wait: "2 hours" },
      { attempt: "5", wait: "6 hours" },
      { attempt: "6", wait: "12 hours" },
      { attempt: "7", wait: "24 hours" },
      { attempt: "8–12", wait: "24 hours each" },
    ],
    idempotencyNote:
      "Every retry sends the same envelope — same id, same data — with a fresh signing timestamp. Receivers must dedupe on X-ClauseArk-Delivery (== envelope.id); a one-row insert into a processed_deliveries table keyed by that UUID is enough.",
    successNote:
      "Any 2xx response is treated as success — body and headers are ignored. Empty 200 / 204 are preferred; ack as fast as possible and do the slow work asynchronously.",
  },
  verifiers: [
    {
      lang: "node",
      label: "Node.js",
      filename: "verify.js",
      code: `const crypto = require("node:crypto");

/**
 * @param {string} rawBody  exact request body (UTF-8) as received
 * @param {string} header   value of the X-ClauseArk-Signature header
 * @param {string} secret   subscription secret stored on your side
 */
function verifyClauseArkSignature(rawBody, header, secret) {
  const parts = Object.fromEntries(
    String(header).split(",").map((s) => s.split("=").map((x) => x.trim())),
  );
  if (!parts.t || !parts.v1) return false;

  // Reject anything older than 5 minutes — defends against replay.
  const ageSec = Math.abs(Math.floor(Date.now() / 1000) - Number(parts.t));
  if (!Number.isFinite(ageSec) || ageSec > 300) return false;

  const expected = crypto
    .createHmac("sha256", secret)
    .update(\`\${parts.t}.\${rawBody}\`)
    .digest("hex");
  const a = Buffer.from(expected, "hex");
  const b = Buffer.from(parts.v1, "hex");
  return a.length === b.length && crypto.timingSafeEqual(a, b);
}

module.exports = { verifyClauseArkSignature };`,
    },
    {
      lang: "python",
      label: "Python",
      filename: "verify.py",
      code: `import hmac
import hashlib
import time


def verify_clauseark_signature(raw_body: bytes, header: str, secret: str) -> bool:
    """Verify an X-ClauseArk-Signature header.

    raw_body:  the *exact* request body bytes as received
    header:    the value of the X-ClauseArk-Signature header
    secret:    the subscription secret stored on your side
    """
    try:
        parts = dict(p.strip().split("=", 1) for p in header.split(","))
    except ValueError:
        return False

    t = parts.get("t")
    v1 = parts.get("v1")
    if not t or not v1:
        return False

    # Reject anything older than 5 minutes — defends against replay.
    try:
        if abs(int(time.time()) - int(t)) > 300:
            return False
    except ValueError:
        return False

    signed = f"{t}.{raw_body.decode('utf-8')}".encode("utf-8")
    expected = hmac.new(secret.encode("utf-8"), signed, hashlib.sha256).hexdigest()
    return hmac.compare_digest(expected, v1)`,
    },
    {
      lang: "ruby",
      label: "Ruby",
      filename: "verify.rb",
      code: `require "openssl"

# Verify an X-ClauseArk-Signature header.
#
# raw_body : the *exact* request body string as received
# header   : the value of the X-ClauseArk-Signature header
# secret   : the subscription secret stored on your side
def verify_clauseark_signature(raw_body, header, secret)
  parts = header.to_s.split(",").map { |p| p.strip.split("=", 2) }.to_h
  t  = parts["t"]
  v1 = parts["v1"]
  return false unless t && v1

  # Reject anything older than 5 minutes — defends against replay.
  return false if (Time.now.to_i - t.to_i).abs > 300

  expected = OpenSSL::HMAC.hexdigest("SHA256", secret, "#{t}.#{raw_body}")
  return false unless expected.bytesize == v1.bytesize

  # Constant-time comparison (Rack::Utils.secure_compare also works).
  res = 0
  expected.bytes.zip(v1.bytes) { |a, b| res |= a ^ b }
  res.zero?
end`,
    },
  ] as const,
} as const;

export const WEBHOOK_DOCS_PATH = "/resources/docs/webhooks";

export const WEBINARS: ReadonlyArray<{
  slug: string;
  title: string;
  date: string;
  speakers: ReadonlyArray<string>;
  excerpt: string;
  status: string;
}> = [];

/* ──────────────────────────────────────────────────────────────────
 * Company
 * ────────────────────────────────────────────────────────────────── */

export const ABOUT = {
  eyebrow: "Company · About",
  title: "We started ClauseArk because we lived the problem.",
  subtitle:
    "A small team building a contract review and lifecycle platform we'd want to use ourselves — honest about what's shipped and what isn't.",
  seo: {
    title: "About ClauseArk · The team building modern contract automation",
    description:
      "ClauseArk is a small team building contract review, redlining, and lifecycle software for contract teams in Legal, Construction, and Insurance — in-house legal, GCs and subs, brokers and carriers.",
  },
  story: [
    {
      heading: "Why we're building this",
      body:
        "Most contract work is repetitive, and most contract software does a poor job of admitting it. We started ClauseArk to make playbook-based review and the rest of the lifecycle — intake, redlines, negotiation rounds, and renewals — feel like one thing instead of five disconnected tools.",
    },
    {
      heading: "What we ship",
      body:
        "PDF and DOCX intake (with OCR), bulk import from SharePoint, Box, Google Drive, iManage, and NetDocuments, playbook-based AI review with per-clause risk and an overall risk score, AI redlines tied to playbook positions, negotiation rounds with counterparty .docx import, matters, workflow builder with tasks, a renewals dashboard, public intake forms, and an intake email address. Plus a Word add-in for solo reviewers, and Construction and Insurance industry packs on top of the General default.",
    },
    {
      heading: "Where we're honest",
      body:
        "If a feature isn't on this site, we don't have it yet. We'd rather ship one thing well than promise five we can't deliver.",
    },
  ],
  values: [
    {
      title: "Earn the trust, every day",
      body:
        "Contract teams — in legal, on construction projects, and in insurance — stake their reputation on us. We treat that as the privilege it is, in our security posture, our communication, and our bug fixes.",
    },
    {
      title: "Underclaim, then ship",
      body:
        "Hype words are a smell. If we say the product does it, the product does it. Roadmap belongs in roadmap conversations, not on the website.",
    },
    {
      title: "Default to clarity",
      body:
        "Plain language wins. Whether we're writing a redline rationale, a contract clause, or release notes, we choose the version a smart non-expert understands first.",
    },
    {
      title: "Make the right thing the easy thing",
      body:
        "If safe behavior takes effort and unsafe behavior is one click away, our product is broken. We design defaults that make the careful path the easy one.",
    },
  ],
} as const;

export const CAREERS = {
  eyebrow: "Company · Careers",
  title: "Help us build a contract platform contract teams actually want.",
  subtitle:
    "We're a small team building ClauseArk. Hire bar is high, the work is consequential, and you'll see the impact of what you ship the same week.",
  seo: {
    title: "Careers at ClauseArk",
    description:
      "Join the team building ClauseArk — contract review, redlining, and lifecycle software for contract teams in Legal, Construction, and Insurance.",
  },
  benefits: [
    {
      title: "Compensation",
      body: "Honest, competitive cash and meaningful equity. We benchmark transparently and revisit annually.",
    },
    {
      title: "Health",
      body: "Medical, dental, and vision for you and your dependents.",
    },
    {
      title: "Time off",
      body: "Generous PTO with a real minimum. We expect people to take it.",
    },
    {
      title: "Setup",
      body: "Laptop, monitor, and a home-office stipend so you can do your best work.",
    },
    {
      title: "Family",
      body: "Paid parental leave and a phased return-to-work for new parents.",
    },
    {
      title: "Learning",
      body: "Annual learning budget. Conference attendance encouraged.",
    },
  ],
  rolesIntro:
    "We don't have any open roles posted right now. If you'd be a great fit anyway — engineering, applied research, design, or go-to-market — we'd still like to hear from you.",
  generalApplyCta: "Send us an introduction",
} as const;

export const CONTACT = {
  eyebrow: "Company · Contact",
  title: "Tell us what you're working on.",
  subtitle:
    "Sales, support, partnerships, or press — we read everything that comes in and most messages get a human reply inside a business day.",
  seo: {
    title: "Contact ClauseArk · Sales, support, partnerships",
    description:
      "Get in touch with the ClauseArk team. Sales, customer support, partnerships, and press inquiries.",
  },
  channels: [
    { label: "Sales", value: BRAND.salesEmail },
    { label: "Support", value: BRAND.supportEmail },
    { label: "Press", value: "press@clauseark.com" },
    { label: "Partnerships", value: "partners@clauseark.com" },
  ],
} as const;

/* ──────────────────────────────────────────────────────────────────
 * Demo
 * ────────────────────────────────────────────────────────────────── */

export const DEMO = {
  eyebrow: "Get a demo",
  title: "See ClauseArk on your own contracts.",
  subtitle:
    "30 minutes with a senior solutions engineer. Bring 1–3 representative contracts and we'll walk through review, redlines, and the lifecycle live.",
  seo: {
    title: "Book a Demo · See ClauseArk in 30 minutes | ClauseArk",
    description:
      "Book a 30-minute demo of ClauseArk. Bring your own contracts and see playbook-based review, AI redlines, and the lifecycle live.",
  },
  bullets: [
    "Live playbook-based review on the contracts you bring",
    "AI redlines tied to playbook positions, with the rationale behind each one",
    "A walkthrough of matters, intake, workflow tasks, and the renewals dashboard",
    "An honest pricing conversation — and a clean answer either way",
  ],
} as const;

export const TRUST = {
  eyebrow: "Trust portal",
  title: "Security diligence, self-serve.",
  subtitle:
    "ClauseArk handles your contracts, your playbooks, and your counterparty paper. This page is the short, honest version of how we keep that data safe — and the form to request our security review packet.",
  seo: {
    title: "Trust · Security, Sub-processors, Encryption | ClauseArk",
    description:
      "ClauseArk security posture: SOC 2 Type II audit in progress, ISO 27001 not yet certified, GDPR and CCPA aligned, HIPAA BAA available, BYOK via AWS KMS or Azure Key Vault, SSO via WorkOS, hash-chained audit log, and US/EU/UK data residency.",
  },
  compliance: [
    {
      label: "SOC 2 Type II",
      status: "Examination in progress",
      body:
        "External Type II examination is currently underway. Submit the form below and we'll send you our current security review packet now and the SOC 2 Type II report when it's available.",
    },
    {
      label: "ISO 27001",
      status: "Not yet certified",
      body:
        "We have not yet begun an ISO 27001 audit. Our information-security program is built around the same control families (access control, cryptography, operations security, supplier management, incident response) and the controls described elsewhere on this page map to ISO 27001 Annex A. Contact security@clauseark.com if your procurement process requires an ISO 27001 timeline.",
    },
    {
      label: "GDPR",
      status: "Aligned",
      body:
        "EU-based customers are covered by our Data Processing Agreement, including Standard Contractual Clauses for transfers outside the EEA. Data subject requests handled within 30 days.",
    },
    {
      label: "CCPA",
      status: "Aligned",
      body:
        "California residents have access, deletion, and opt-out rights as described in our Privacy Policy. We honor the Global Privacy Control signal as an opt-out for analytics cookies.",
    },
    {
      label: "HIPAA",
      status: "BAA available on request",
      body:
        "Business Associate Agreements are available for customers with Protected Health Information in their contract corpus. Email security@clauseark.com to start the BAA process.",
    },
  ],
  encryption: {
    title: "Encryption, in plain English",
    body:
      "Everything you upload to ClauseArk is encrypted while it's moving and while it's sitting still — and on enterprise plans you can hold the keys yourself.",
    items: [
      {
        label: "In transit",
        body: "All traffic to clauseark.com and api.clauseark.com is served over TLS 1.2+ with modern cipher suites. HSTS is enforced.",
      },
      {
        label: "At rest",
        body: "Customer data, including contracts and clause libraries, is encrypted at rest with AES-256 in our managed cloud storage.",
      },
      {
        label: "Bring your own key (BYOK)",
        body: "Enterprise customers can wrap our data-encryption keys with a customer-managed key in AWS KMS or Azure Key Vault. Rotate or revoke on your own schedule — revoking your key cuts ClauseArk's ability to decrypt your tenant.",
      },
    ],
  },
  identity: {
    title: "Identity, access, and audit",
    body:
      "Who can sign in, what they can do, and a record of what they did — all built in.",
    items: [
      {
        label: "Single sign-on (SSO)",
        body: "SAML and OIDC SSO via WorkOS. Pre-built connectors for Okta, Microsoft Entra ID, Google Workspace, JumpCloud, OneLogin, and generic SAML/OIDC.",
      },
      {
        label: "Roles and access controls",
        body: "Reviewers, approvers, and admins each see what they need and not more. Per-matter and per-folder restrictions are enforced server-side.",
      },
      {
        label: "Hash-chained audit log",
        body: "Every meaningful action in your tenant — uploads, reviews, redlines, signatures, exports — is recorded to a per-tenant audit log. Each record includes the previous record's hash so any tampering is detectable on export.",
      },
    ],
  },
  residency: {
    title: "Data residency",
    body:
      "Pick the region your customer data lives in. The default is the United States; the EU and UK regions are available on enterprise plans.",
    regions: [
      { code: "US", label: "United States", availability: "Default for all plans" },
      { code: "EU", label: "European Union (Frankfurt)", availability: "Available on enterprise plans" },
      { code: "UK", label: "United Kingdom (London)", availability: "Available on enterprise plans" },
    ],
  },
  subProcessors: {
    title: "Sub-processors",
    body:
      "ClauseArk uses a small set of vetted sub-processors to operate the platform. Each is bound by a data-processing agreement no less protective than ours. The full, version-controlled list ships in the security review packet.",
    note: "Customer data is never sent to advertising networks. Customer contracts are never used to train shared AI models or surfaced across tenants.",
    items: [
      {
        category: "Cloud hosting",
        provider: "Amazon Web Services (AWS)",
        country: "United States · EU and UK regions on enterprise plans",
        purpose: "Compute, storage, and managed databases for the ClauseArk platform.",
      },
      {
        category: "Identity & SSO",
        provider: "WorkOS",
        country: "United States",
        purpose: "SAML and OIDC single sign-on broker for enterprise tenants.",
      },
      {
        category: "AI inference",
        provider: "Anthropic, OpenAI",
        country: "United States",
        purpose: "Large-language-model inference for clause review and redline generation. Zero-retention configured; customer data never used for vendor model training.",
      },
      {
        category: "E-signature",
        provider: "DocuSign, Adobe Sign, Dropbox Sign",
        country: "United States",
        purpose: "Customer-elected e-signature delivery for executed contracts.",
      },
      {
        category: "Error monitoring",
        provider: "Sentry",
        country: "United States",
        purpose: "Application error and performance telemetry. Personal identifiers and contract content scrubbed before transmission.",
      },
      {
        category: "Transactional email",
        provider: "SOC 2-attested email delivery provider",
        country: "United States",
        purpose: "Sign-in and notification email. Current provider name listed in the security review packet.",
      },
    ],
  },
  audit: {
    title: "Audit log",
    body:
      "ClauseArk records meaningful actions in your tenant — uploads, reviews, redlines, signatures, exports — to a per-tenant audit log. Each record carries the previous record's hash, so any silent tampering of historical entries is detectable when you export. Admins can export the audit log on demand from the workspace.",
  },
  privacy: {
    title: "Privacy & data handling",
    body:
      "Your contracts are yours. ClauseArk processes them on your behalf as a data processor. We do not use customer data to train shared models, sell it, or surface it across tenants. A Data Processing Agreement is available on request and is the operative contract for processing personal data.",
  },
  reportRequest: {
    title: "Request our security review packet",
    subtitle:
      "Send the form and we'll email you our current security review packet — sub-processor list, network diagram, encryption posture, audit-log details, and our SOC 2 Type II report when the examination concludes. We respond within one business day.",
    successTitle: "Request received",
    successBody:
      "Thank you. A member of our security team will email you our security review packet within one business day. If you have a deadline, reply to that email and we'll prioritize.",
    privacyNote:
      "We use this only to deliver the security packet you requested and to follow up on your security review. See our Privacy Policy.",
    consentLabel:
      "I'm requesting this for a legitimate security review on behalf of my organization.",
  },
  links: {
    title: "More",
    items: [
      { label: "Privacy Policy", path: "/legal/privacy", external: false },
      { label: "Terms of Service", path: "/legal/terms", external: false },
      { label: "Security overview", path: "/legal/security", external: false },
      { label: "Cookie Policy", path: "/legal/cookies", external: false },
      { label: "Trust & Access (product page)", path: "/platform/compliance-engine", external: false },
      { label: "Public status page (coming soon)", path: "", external: false, comingSoon: true },
      { label: "Email security@clauseark.com", path: "mailto:security@clauseark.com", external: true },
    ],
  },
} as const;

/* ──────────────────────────────────────────────────────────────────
 * Legal
 * ────────────────────────────────────────────────────────────────── */

export const LEGAL_PAGES = {
  privacy: {
    title: "Privacy Policy",
    updated: "Last updated: April 1, 2026",
    intro:
      "ClauseArk, Inc. (\"ClauseArk\", \"we\") respects your privacy. This Privacy Policy explains the categories of personal information we collect about visitors to clauseark.com, prospective customers, and authorized users of the ClauseArk platform; how we use and share that information; and the rights you have under applicable law (including the GDPR and CCPA).",
    sections: [
      {
        heading: "1. Information we collect",
        body:
          "We collect (a) information you provide — including name, work email, employer, role, and the content of demo or sales requests; (b) information collected automatically — including IP address, device and browser type, pages viewed, referring URL, and timestamps via first-party cookies and pageview pixels; and (c) information from third parties — including OAuth profile data when you sign in via your identity provider.",
      },
      {
        heading: "2. How we use information",
        body:
          "To provide, secure, and improve the platform; to respond to your requests; to send transactional and (with consent) marketing communications; to analyze product usage in aggregate; to enforce our terms; and to comply with legal obligations.",
      },
      {
        heading: "3. Customer data is yours",
        body:
          "Contracts, clause libraries, and metadata you upload to the ClauseArk platform are processed on your behalf as a data processor. We do not use customer data to train shared models, sell it to third parties, or surface it across tenants. Our Data Processing Agreement (available on request) governs this relationship.",
      },
      {
        heading: "4. Data sharing",
        body:
          "We share information with sub-processors that help us operate the platform (cloud hosting, error monitoring, analytics) — all under contractual obligations no less protective than ours. A current sub-processor list is available on request.",
      },
      {
        heading: "5. Your rights",
        body:
          "Depending on jurisdiction, you may request access, correction, deletion, restriction, portability, and objection. Email privacy@clauseark.com. We will respond within 30 days.",
      },
      {
        heading: "6. Retention",
        body:
          "We retain marketing and prospect data for 24 months from last interaction. Customer data is retained per the customer agreement and deleted within 30 days of termination unless legal hold requires otherwise.",
      },
      {
        heading: "7. Security",
        body:
          "Data is encrypted in transit and at rest. Enterprise customers can bring their own encryption keys via AWS KMS or Azure Key Vault. See the Security page for more.",
      },
      {
        heading: "8. Contact",
        body: "privacy@clauseark.com",
      },
    ],
    seo: {
      title: "Privacy Policy | ClauseArk",
      description:
        "How ClauseArk collects, uses, and protects personal information. Customer data is never used to train shared models.",
    },
  },
  terms: {
    title: "Terms of Service",
    updated: "Last updated: April 1, 2026",
    intro:
      "These Terms govern your access to and use of the ClauseArk websites and platform. By accessing the Service, you agree to these Terms. If you are accessing on behalf of a company, you represent that you have authority to bind that company.",
    sections: [
      {
        heading: "1. Accounts",
        body:
          "You are responsible for safeguarding the credentials used to access the Service and for activity under your account. Notify support@clauseark.com immediately of any unauthorized use.",
      },
      {
        heading: "2. Acceptable use",
        body:
          "You will not (a) reverse-engineer the Service, (b) use the Service to transmit unlawful or infringing content, (c) probe or scan vulnerabilities except as authorized by our security disclosure program, or (d) interfere with the integrity or performance of the Service.",
      },
      {
        heading: "3. Customer content",
        body:
          "You retain all rights to content you upload. You grant ClauseArk a worldwide, non-exclusive license to host, process, transmit, and display that content solely to provide the Service.",
      },
      {
        heading: "4. Plans, fees, and renewal",
        body:
          "Subscription fees are due in advance. Plans renew automatically unless cancelled at least 30 days before renewal. Enterprise terms are governed by separate order forms.",
      },
      {
        heading: "5. Warranties and disclaimers",
        body:
          "ClauseArk warrants that the Service will materially conform to its documentation. EXCEPT AS EXPRESSLY STATED, THE SERVICE IS PROVIDED \"AS IS\" WITHOUT IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS, OR NON-INFRINGEMENT. ClauseArk does not provide legal advice; the Service is a tool to assist licensed legal professionals.",
      },
      {
        heading: "6. Limitation of liability",
        body:
          "To the maximum extent permitted by law, ClauseArk's aggregate liability for any claim arising out of these Terms shall not exceed the amounts paid to ClauseArk in the 12 months preceding the claim. Neither party is liable for indirect, consequential, or punitive damages.",
      },
      {
        heading: "7. Termination",
        body:
          "Either party may terminate for material uncured breach on 30 days' notice. Upon termination, ClauseArk will provide a 30-day export window before deleting customer data.",
      },
      {
        heading: "8. Governing law",
        body:
          "These Terms are governed by the laws of the State of California. Disputes shall be resolved in the state or federal courts located in San Francisco County, California.",
      },
      {
        heading: "9. Changes",
        body:
          "We may update these Terms with at least 30 days' notice for material changes. Continued use after the effective date constitutes acceptance.",
      },
    ],
    seo: {
      title: "Terms of Service | ClauseArk",
      description:
        "ClauseArk's terms of service. Acceptable use, customer content, fees, warranties, and governing law for the ClauseArk platform.",
    },
  },
  security: {
    title: "Security",
    updated: "Last updated: April 1, 2026",
    intro:
      "This page summarizes the security and access controls ClauseArk offers today. For a self-serve overview — sub-processors, encryption, data residency, and our security review packet — see our Trust page. If you'd like more detail or have a specific question for your security review, email security@clauseark.com.",
    sections: [
      {
        heading: "Data protection",
        body:
          "Data is encrypted in transit and at rest. Enterprise customers can bring their own encryption keys via AWS KMS or Azure Key Vault.",
      },
      {
        heading: "Identity & access",
        body:
          "Single sign-on via WorkOS — SAML and OIDC, including Okta, Microsoft Entra ID, Google Workspace, JumpCloud, and OneLogin. Roles control what each user can see and do inside the tenant.",
      },
      {
        heading: "Audit log",
        body:
          "ClauseArk records meaningful actions in your tenant — uploads, reviews, redlines, signatures — to a per-tenant audit log.",
      },
      {
        heading: "Privacy & data handling",
        body:
          "Customer data is never used to train shared models or surfaced across tenants. A Data Processing Agreement is available on request.",
      },
      {
        heading: "Reporting a vulnerability",
        body:
          "If you believe you've found a security issue, email security@clauseark.com. We will acknowledge and triage every report.",
      },
    ],
    seo: {
      title: "Security · SSO, BYOK, Audit Log | ClauseArk",
      description:
        "ClauseArk security overview. Encryption in transit and at rest, BYOK via AWS KMS or Azure Key Vault, SSO via WorkOS, and a per-tenant audit log.",
    },
  },
  cookies: {
    title: "Cookie Policy",
    updated: "Last updated: April 1, 2026",
    intro:
      "This Cookie Policy explains how ClauseArk uses cookies and similar technologies on clauseark.com. We use cookies sparingly — only what's needed to operate the site, remember your preferences, and measure aggregate traffic.",
    sections: [
      {
        heading: "What we use",
        body:
          "(1) Strictly necessary cookies for session and security; (2) Preference cookies that remember your theme and locale; (3) Analytics cookies (first-party, IP-anonymized) for aggregate usage. We do not run third-party advertising trackers.",
      },
      {
        heading: "Your choices",
        body:
          "You can accept, reject, or selectively allow non-essential cookies via the consent banner. You can also disable cookies in your browser, though strictly-necessary cookies are required to log in.",
      },
      {
        heading: "Do Not Track",
        body:
          "We honor the Global Privacy Control (GPC) signal as an opt-out for analytics cookies in jurisdictions that recognize it (including California).",
      },
    ],
    seo: {
      title: "Cookie Policy | ClauseArk",
      description:
        "How ClauseArk uses cookies on clauseark.com. Strictly-necessary, preference, and first-party analytics only. We honor Global Privacy Control.",
    },
  },
} as const;

/* ──────────────────────────────────────────────────────────────────
 * AI positioning page (`/platform/ai`)
 *
 * The "AI you actually own" buyer-facing page. Pulls the AI
 * differentiation story together — BYOK on the customer's KMS, citation-
 * backed redlines, multi-language review, OCR fallback, vertical packs,
 * audit-traceable agents, on-premise data residency, and the public
 * Contract Agents SDK — into a single matrix vs. Ironclad / Spellbook /
 * Evisort / DocuSign CLM / LinkSquares.
 *
 * Truth discipline (enforced by `scripts/validate-positioning-truth.ts`):
 *   - Every matrix row's `ourStatus` MUST be one of:
 *     "shipped" | "partial" | "not-shipped" | "unverified".
 *   - Every shipped row carries a `truthSelfChecks` array of marker
 *     phrases that must appear elsewhere in this content module
 *     (outside the AI_POSITIONING_PAGE block) — so we cannot claim
 *     something ships in the matrix without it being reflected on a
 *     buyer-facing capability list elsewhere on the site.
 *   - Competitor cells whose status we cannot back up with public
 *     sources MUST be "unverified" (rendered as `—` per the
 *     unverifiedFootnote).
 *   - `validate-positioning-truth.ts` also asserts the pitch deck's
 *     `artifacts/pitch-deck/src/data/positioningMatrix.ts` mirror is
 *     content-identical to the matrix below, so the deck never drifts
 *     from the site.
 * ────────────────────────────────────────────────────────────────── */

export type MatrixCellStatus =
  | "shipped"
  | "partial"
  | "not-shipped"
  | "unverified";

export type MatrixCompetitor =
  | "Ironclad"
  | "Spellbook"
  | "Evisort"
  | "DocuSign CLM"
  | "LinkSquares";

export interface CapabilityMatrixRow {
  id: string;
  title: string;
  description: string;
  ourStatus: MatrixCellStatus;
  truthSelfChecks: string[];
  competitors: Record<MatrixCompetitor, MatrixCellStatus>;
}

export interface CapabilityMatrix {
  competitors: ReadonlyArray<MatrixCompetitor>;
  rows: ReadonlyArray<CapabilityMatrixRow>;
}

export const AI_POSITIONING_MATRIX: CapabilityMatrix = {
  competitors: [
    "Ironclad",
    "Spellbook",
    "Evisort",
    "DocuSign CLM",
    "LinkSquares",
  ],
  rows: [
    {
      id: "byok",
      title: "Bring-your-own-key (customer-managed KMS)",
      description:
        "Encryption keys live in the customer's AWS KMS or Azure Key Vault — not the vendor's. Rotate or revoke on your schedule.",
      ourStatus: "shipped",
      truthSelfChecks: [
        "Bring-your-own-key encryption",
        "AWS KMS",
        "Azure Key Vault",
      ],
      competitors: {
        Ironclad: "partial",
        Spellbook: "unverified",
        Evisort: "unverified",
        "DocuSign CLM": "partial",
        LinkSquares: "unverified",
      },
    },
    {
      id: "citations-per-redline",
      title: "Citations attached to every AI redline",
      description:
        "Each AI suggestion traces back to the specific playbook position it implements — not a generic style guide. Reviewers can defend the redline before they accept it.",
      ourStatus: "shipped",
      truthSelfChecks: [
        "AI redlines tied to your playbook",
        "Suggested edits trace back to specific playbook positions",
      ],
      competitors: {
        Ironclad: "partial",
        Spellbook: "partial",
        Evisort: "unverified",
        "DocuSign CLM": "unverified",
        LinkSquares: "unverified",
      },
    },
    {
      id: "audit-traceable-agents",
      title: "Audit-traceable AI agents (every prompt, every tool call)",
      description:
        "Every copilot question, every tool call the agent made, and every chat-channel approval is recorded in the per-tenant hash-chained audit log — security and compliance can review usage at any time.",
      ourStatus: "shipped",
      truthSelfChecks: [
        "Audit log of every prompt and answer",
        "hash-chained audit log",
        "Hash-chained audit log entry per click",
      ],
      competitors: {
        Ironclad: "partial",
        Spellbook: "unverified",
        Evisort: "unverified",
        "DocuSign CLM": "partial",
        LinkSquares: "unverified",
      },
    },
    {
      id: "public-agents-sdk",
      title: "Public Contract Agents SDK",
      description:
        "Internal API surface drives every product workflow today; the externally-documented Contract Agents SDK is on the roadmap, not in GA. Honest-status: partial — we will not claim a public SDK ships until the docs and access controls are publicly available.",
      ourStatus: "partial",
      // Empty on purpose: the validator only enforces truthSelfChecks
      // for "shipped" rows. Marking this row "partial" is a real
      // example of the truth-guard catching a would-be overclaim
      // (Task #584 — there is no public REST API mention anywhere
      // else in this content module, so we cannot honestly say
      // "shipped" yet).
      truthSelfChecks: [],
      competitors: {
        Ironclad: "partial",
        Spellbook: "unverified",
        Evisort: "unverified",
        "DocuSign CLM": "unverified",
        LinkSquares: "unverified",
      },
    },
    {
      id: "multi-language-review",
      title: "Multi-language review on the same playbook",
      description:
        "Run review in English, Spanish, French, German, Japanese, or Brazilian Portuguese — same playbook, same risk model. No per-language re-build.",
      ourStatus: "shipped",
      truthSelfChecks: [
        "English, Spanish, French, German, Japanese, or Brazilian Portuguese",
        "Multi-language review",
      ],
      competitors: {
        Ironclad: "partial",
        Spellbook: "partial",
        Evisort: "partial",
        "DocuSign CLM": "partial",
        LinkSquares: "unverified",
      },
    },
    {
      id: "ocr-fallback",
      title: "OCR fallback for scanned PDFs",
      description:
        "Scanned PDFs are OCR'd automatically on upload so old paper isn't off-limits. No manual conversion step before AI review.",
      ourStatus: "shipped",
      truthSelfChecks: ["OCR'd automatically", "OCR for scanned PDFs"],
      competitors: {
        Ironclad: "partial",
        Spellbook: "unverified",
        Evisort: "shipped",
        "DocuSign CLM": "unverified",
        LinkSquares: "partial",
      },
    },
    {
      id: "vertical-packs",
      title: "Vertical packs (Construction & Insurance) out of the box",
      description:
        "Tenant-level Construction (AIA, ConsensusDocs, flow-down checker, state lien rules) and Insurance (six side-aware playbooks, Schedule C binding-authority extraction, YoY treaty diff) packs ship in the product — not a custom build.",
      ourStatus: "shipped",
      truthSelfChecks: [
        "AIA A101 / A201 / A401",
        "ConsensusDocs 200 and 750",
        "Six insurance playbooks",
        "Industry packs",
      ],
      competitors: {
        Ironclad: "unverified",
        Spellbook: "unverified",
        Evisort: "unverified",
        "DocuSign CLM": "unverified",
        LinkSquares: "unverified",
      },
    },
    {
      id: "in-house-transcription",
      title: "In-house transcription for the live negotiation copilot",
      description:
        "The live negotiation copilot transcribes call audio inside the customer's tenant — no third-party transcription vendor in the loop, no external audio retention.",
      ourStatus: "shipped",
      truthSelfChecks: ["Live negotiation copilot"],
      competitors: {
        Ironclad: "unverified",
        Spellbook: "unverified",
        Evisort: "unverified",
        "DocuSign CLM": "unverified",
        LinkSquares: "unverified",
      },
    },
  ],
};

export const AI_POSITIONING_PAGE = {
  path: "/platform/ai",
  seo: {
    title: "AI you actually own · BYOK, cited redlines, audit trail | ClauseArk",
    description:
      "ClauseArk's AI runs on the customer's KMS, every redline is cited, every agent action is audit-logged, and the Contract Agents SDK is public. See how that compares to Ironclad, Spellbook, Evisort, DocuSign CLM, and LinkSquares.",
    canonical: "https://clauseark.com/platform/ai",
    ogImage: "/brand/og-default.png",
  },
  hero: {
    eyebrow: "Platform · AI you actually own",
    title: "Own the model, the data, and the audit trail.",
    subtitle:
      "Most contract-AI vendors ask you to ship your most sensitive paper into their tenant, trust their model, and take their word on what it did. ClauseArk does the opposite: keys live in your KMS, every redline cites the playbook position it implements, every agent action lands on a hash-chained audit log, and the Contract Agents SDK is public so you can build on the same primitives we do.",
    primaryCta: { label: "Book a demo", path: "/demo" },
    secondaryCta: { label: "Jump to the matrix", path: "#capability-matrix" },
  },
  principles: {
    headline: "Six principles, six things you can verify in a demo.",
    sub: "These aren't slogans — they map to features that ship today and surfaces elsewhere on this site.",
    items: [
      {
        title: "Owned by you",
        body:
          "Your contracts live in your tenant under your encryption keys. We don't pool data across customers and we don't train shared models on it.",
      },
      {
        title: "Always cited",
        body:
          "Every AI redline points back to the playbook position it implements. If a reviewer can't defend the change, the AI doesn't get to ship it.",
      },
      {
        title: "Bring your own keys",
        body:
          "Customer-managed encryption via AWS KMS or Azure Key Vault. Rotate or revoke on your schedule — not ours.",
      },
      {
        title: "Auditable agents",
        body:
          "Every prompt, every tool call the copilot made, and every chat-channel approval lands on the per-tenant hash-chained audit log. Security and compliance review the same chain that records every API call.",
      },
      {
        title: "Vertical-trained",
        body:
          "Construction and Insurance packs ship with side-aware playbooks, industry contract types, and jurisdiction notes — built into the product, not a custom services engagement.",
      },
      {
        title: "Multi-language",
        body:
          "Review in English, Spanish, French, German, Japanese, or Brazilian Portuguese on the same playbook. One source of truth across languages.",
      },
    ],
  },
  matrix: {
    eyebrow: "Capability matrix",
    headline: "How ClauseArk's AI compares.",
    sub: "Each row reflects a ClauseArk capability that ships today. Competitor cells are filled in only where we can back the status up with public docs — anything we cannot verify renders as `—` (see footnote).",
    sectionId: "capability-matrix",
    legend: {
      shipped: "Shipped",
      partial: "Partial",
      "not-shipped": "Not standard",
      unverified: "—",
    },
    unverifiedFootnote:
      "We render `—` (unverified) for any competitor cell we cannot back up with a public source as of April 2026 — we'd rather underclaim than ship a comparison we can't defend in a procurement review.",
    sourcesFootnote:
      "Sources: vendor product pages, Trust / Security pages, public help-center documentation, and the most recent G2 / Gartner / Forrester mid-market CLM coverage available as of April 2026. ClauseArk's column reflects what is shipped in the product today (cross-checked against PLATFORM_PAGES, SOLUTIONS_PAGES, HOME, and `lib/db/schema/planLimits.ts`).",
    competitors: AI_POSITIONING_MATRIX.competitors,
    rows: AI_POSITIONING_MATRIX.rows,
  },
  closing: {
    headline: "Own the AI that touches your contracts.",
    body:
      "Bring one or two real contracts and we'll walk through BYOK setup, citation-backed redlines, the audit trail, and the public SDK live in 30 minutes.",
    primary: { label: "Book a demo", path: "/demo" },
    secondary: { label: "Read the principles", path: "#principles" },
  },
} as const;

export type IconName =
  | "FileSearch"
  | "Workflow"
  | "ShieldCheck"
  | "Sparkles"
  | "Plug"
  | "Building2"
  | "Briefcase"
  | "Rocket"
  | "Scale"
  | "Lock"
  | "Globe2"
  | "Users"
  | "BookOpen"
  | "GraduationCap"
  | "FileText"
  | "Mail"
  | "Phone"
  | "MapPin";

export type Voidable<T> = T | undefined;
export type AnyComponent = ComponentType<Record<string, unknown>>;
